import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../components/context/AuthContext";
import { useBookings } from "../components/context/BookingContext";
import { useNavigate } from "react-router-dom";

import SkeletonLoader from "../components/common/SkeletonLoader";

const BOOKING_STATUS_OPTIONS = [
  { label: "Pending", value: "Pending", icon: "⏳" },
  { label: "Confirmed", value: "Confirmed", icon: "✅" },
  { label: "Cancelled", value: "Cancelled", icon: "❌" },
  { label: "Completed", value: "Completed", icon: "🏁" }
];

const GENERAL_STATUS_OPTIONS = [
  { label: "Pending", value: "Pending", icon: "⏳" },
  { label: "In Progress", value: "In Progress", icon: "🔄" },
  { label: "Resolved", value: "Resolved", icon: "✅" },
  { label: "Cancelled", value: "Cancelled", icon: "❌" }
];

function HKStatusDropdown({ value, options, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentOpt = options.find((o) => o.value === value) || options[0] || { label: value, value, icon: "⏳" };

  return (
    <div className={`hk-custom-dropdown-container ${isOpen ? "hk-custom-dropdown-container--open" : ""}`} ref={dropdownRef} style={{ zIndex: isOpen ? 99999 : 10, position: "relative" }}>
      <button
        type="button"
        className={`hk-custom-dropdown-trigger hk-status-badge--${(value || 'pending').toLowerCase().replace(/\s+/g, '')}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className="d-flex align-items-center gap-1">
          <span style={{ fontSize: "0.85rem" }}>{currentOpt.icon}</span>
          <span className="fw-semibold">{currentOpt.label}</span>
        </span>
        <i className={`fa fa-chevron-down ms-2 ${isOpen ? "rotate-180" : ""}`} style={{ fontSize: "0.65rem", transition: "transform 0.2s ease" }} />
      </button>

      {isOpen && (
        <div className="hk-custom-dropdown-menu shadow-lg">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`hk-custom-dropdown-item ${opt.value === value ? "hk-custom-dropdown-item--active" : ""}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              <span className="me-2" style={{ fontSize: "0.85rem" }}>{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout, isLoading } = useAuth();
  const {
    bookings, rooms, callbacks, supportQueries, coupons,
    isLoadingBookings, isLoadingRooms, isLoadingCallbacks, isLoadingSupportQueries, isLoadingCoupons,
    refreshBookings, refreshRooms, refreshCallbacks, refreshSupportQueries, refreshCoupons,
    adminUpdateStatus, adminUpdateCallbackStatus, adminUpdateSupportStatus,
    adminAddRoom, adminUpdateRoom, adminCreateAdmin, adminCreateCoupon, adminUpdateCouponStatus, adminDeleteCoupon
  } = useBookings();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [updatingId, setUpdatingId] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Add Room form state
  const [newRoomType, setNewRoomType] = useState("");
  const [newRoomPrice, setNewRoomPrice] = useState("");
  const [newRoomInventory, setNewRoomInventory] = useState("");
  const [addRoomMsg, setAddRoomMsg] = useState("");
  const [isAddingRoom, setIsAddingRoom] = useState(false);

  // Edit Room modal state
  const [editingRoom, setEditingRoom] = useState(null);
  const [editRoomType, setEditRoomType] = useState("");
  const [editRoomPrice, setEditRoomPrice] = useState("");
  const [editRoomInventory, setEditRoomInventory] = useState("");
  const [editBookedCount, setEditBookedCount] = useState("");
  const [editRoomStatus, setEditRoomStatus] = useState("Available");
  const [editDiscountPercent, setEditDiscountPercent] = useState(0);
  const [editPromoText, setEditPromoText] = useState("");
  const [isSavingRoom, setIsSavingRoom] = useState(false);
  const [editRoomMsg, setEditRoomMsg] = useState("");
  const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);

  // Create Admin form state
  const [newAdminName, setNewAdminName] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPhone, setNewAdminPhone] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [newAdminConfirm, setNewAdminConfirm] = useState("");
  const [showAdminPwd, setShowAdminPwd] = useState(false);
  const [isCreatingAdmin, setIsCreatingAdmin] = useState(false);
  const [createAdminMsg, setCreateAdminMsg] = useState("");
  const [isAddAdminModalOpen, setIsAddAdminModalOpen] = useState(false);

  // Create Coupon form state
  const [newCouponCode, setNewCouponCode] = useState("");
  const [newCouponType, setNewCouponType] = useState("percentage");
  const [newCouponValue, setNewCouponValue] = useState("");
  const [newCouponMinAmount, setNewCouponMinAmount] = useState("");
  const [newCouponMaxUses, setNewCouponMaxUses] = useState("");
  const [newCouponValidFrom, setNewCouponValidFrom] = useState("");
  const [newCouponValidTo, setNewCouponValidTo] = useState("");
  const [isCreatingCoupon, setIsCreatingCoupon] = useState(false);
  const [createCouponMsg, setCreateCouponMsg] = useState("");

  // Resort Settings form state
  const [resortPhone, setResortPhone] = useState(() => {
    try {
      const s = localStorage.getItem("hk_resort_settings");
      if (s) return JSON.parse(s).phone || "+91 9602588882";
    } catch (e) {}
    return "+91 9602588882";
  });
  const [resortWhatsapp, setResortWhatsapp] = useState(() => {
    try {
      const s = localStorage.getItem("hk_resort_settings");
      if (s) return JSON.parse(s).whatsapp || "919602588882";
    } catch (e) {}
    return "919602588882";
  });
  const [resortEmail, setResortEmail] = useState(() => {
    try {
      const s = localStorage.getItem("hk_resort_settings");
      if (s) return JSON.parse(s).email || "info@harikunjresort.com";
    } catch (e) {}
    return "info@harikunjresort.com";
  });
  const [resortAddress, setResortAddress] = useState(() => {
    try {
      const s = localStorage.getItem("hk_resort_settings");
      if (s) return JSON.parse(s).address || "Gopal Badi, Chopasni, Jodhpur, 342008 Rajasthan";
    } catch (e) {}
    return "Gopal Badi, Chopasni, Jodhpur, 342008 Rajasthan";
  });
  const [bookingEmail, setBookingEmail] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s && JSON.parse(s).bookingEmail) return JSON.parse(s).bookingEmail; } catch (e) {}
    return "reservation@harikunjresort.com & booking@harikunjresort.com";
  });
  const [techEmail, setTechEmail] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s && JSON.parse(s).techEmail) return JSON.parse(s).techEmail; } catch (e) {}
    return "reservation@harikunjresort.com & tech@harikunjresort.com";
  });
  const [generalEmail, setGeneralEmail] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s && JSON.parse(s).generalEmail) return JSON.parse(s).generalEmail; } catch (e) {}
    return "reservation@harikunjresort.com & info@harikunjresort.com";
  });

  // Social Links & Visibility Toggles
  const [resortInstagram, setResortInstagram] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s) return JSON.parse(s).instagram || "https://www.instagram.com/harikunjresort/"; } catch (e) {}
    return "https://www.instagram.com/harikunjresort/";
  });
  const [resortFacebook, setResortFacebook] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s) return JSON.parse(s).facebook || "https://www.facebook.com/harikunjresort/"; } catch (e) {}
    return "https://www.facebook.com/harikunjresort/";
  });
  const [resortYoutube, setResortYoutube] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s) return JSON.parse(s).youtube || "https://www.youtube.com/@harikunjresort"; } catch (e) {}
    return "https://www.youtube.com/@harikunjresort";
  });
  const [resortLocationUrl, setResortLocationUrl] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s) return JSON.parse(s).locationUrl || "https://www.google.com/maps?q=Gopal+Badi,+Chopasni,+Jodhpur,+342008,+Rajasthan"; } catch (e) {}
    return "https://www.google.com/maps?q=Gopal+Badi,+Chopasni,+Jodhpur,+342008,+Rajasthan";
  });

  const [showInstagram, setShowInstagram] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s && JSON.parse(s).showInstagram !== undefined) return JSON.parse(s).showInstagram; } catch (e) {}
    return true;
  });
  const [showFacebook, setShowFacebook] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s && JSON.parse(s).showFacebook !== undefined) return JSON.parse(s).showFacebook; } catch (e) {}
    return true;
  });
  const [showYoutube, setShowYoutube] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s && JSON.parse(s).showYoutube !== undefined) return JSON.parse(s).showYoutube; } catch (e) {}
    return true;
  });
  const [showLocation, setShowLocation] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s && JSON.parse(s).showLocation !== undefined) return JSON.parse(s).showLocation; } catch (e) {}
    return true;
  });
  const [showFloatingContact, setShowFloatingContact] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s && JSON.parse(s).showFloatingContact !== undefined) return JSON.parse(s).showFloatingContact; } catch (e) {}
    return true;
  });
  const [showNewsletter, setShowNewsletter] = useState(() => {
    try { const s = localStorage.getItem("hk_resort_settings"); if (s && JSON.parse(s).showNewsletter !== undefined) return JSON.parse(s).showNewsletter; } catch (e) {}
    return true;
  });

  // 🔹 Super Admin Popup Controls State
  const defaultAdults = ["Adult 1", "Adult 2", "Adult 3", "Adult 4", "Adult 5+ (Luxury Group)"];
  const defaultChildren = ["Child 0", "Child 1", "Child 2", "Child 3", "Child 4+"];

  const [roomInputs, setRoomInputs] = useState({});

  const [adultOptions, setAdultOptions] = useState(() => {
    try {
      const stored = localStorage.getItem("hk_popup_adult_options");
      return stored ? JSON.parse(stored) : defaultAdults;
    } catch (e) { return defaultAdults; }
  });

  const [childrenOptions, setChildrenOptions] = useState(() => {
    try {
      const stored = localStorage.getItem("hk_popup_children_options");
      return stored ? JSON.parse(stored) : defaultChildren;
    } catch (e) { return defaultChildren; }
  });

  const [newAdultInput, setNewAdultInput] = useState("");
  const [newChildInput, setNewChildInput] = useState("");
  const [popupCtrlMsg, setPopupCtrlMsg] = useState("");

  const handleAddAdultOption = (e) => {
    e.preventDefault();
    if (!newAdultInput.trim()) return;
    const updated = [...adultOptions, newAdultInput.trim()];
    setAdultOptions(updated);
    localStorage.setItem("hk_popup_adult_options", JSON.stringify(updated));
    setNewAdultInput("");
    setPopupCtrlMsg("✅ Adult option added live!");
    setTimeout(() => setPopupCtrlMsg(""), 3000);
  };

  const handleRemoveAdultOption = (index) => {
    const updated = adultOptions.filter((_, i) => i !== index);
    setAdultOptions(updated);
    localStorage.setItem("hk_popup_adult_options", JSON.stringify(updated));
    setPopupCtrlMsg("✅ Adult option removed live!");
    setTimeout(() => setPopupCtrlMsg(""), 3000);
  };

  const handleAddChildOption = (e) => {
    e.preventDefault();
    if (!newChildInput.trim()) return;
    const updated = [...childrenOptions, newChildInput.trim()];
    setChildrenOptions(updated);
    localStorage.setItem("hk_popup_children_options", JSON.stringify(updated));
    setNewChildInput("");
    setPopupCtrlMsg("✅ Child option added live!");
    setTimeout(() => setPopupCtrlMsg(""), 3000);
  };

  const handleRemoveChildOption = (index) => {
    const updated = childrenOptions.filter((_, i) => i !== index);
    setChildrenOptions(updated);
    localStorage.setItem("hk_popup_children_options", JSON.stringify(updated));
    setPopupCtrlMsg("✅ Child option removed live!");
    setTimeout(() => setPopupCtrlMsg(""), 3000);
  };

  const handleResetPopupOptions = () => {
    setAdultOptions(defaultAdults);
    setChildrenOptions(defaultChildren);
    localStorage.setItem("hk_popup_adult_options", JSON.stringify(defaultAdults));
    localStorage.setItem("hk_popup_children_options", JSON.stringify(defaultChildren));
    setPopupCtrlMsg("✅ Reset to default resort dropdown values!");
    setTimeout(() => setPopupCtrlMsg(""), 3000);
  };

  // VIP Subscribers State
  const [subscribers, setSubscribers] = useState(() => {
    try {
      const saved = localStorage.getItem("hk_vip_subscribers");
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return [
      { id: "SUB-101", email: "ashokumar80783@gmail.com", subscribedAt: new Date().toISOString() },
      { id: "SUB-102", email: "royal.guest@gmail.com", subscribedAt: new Date(Date.now() - 86400000).toISOString() }
    ];
  });

  useEffect(() => {
    const handleStorage = () => {
      try {
        const saved = localStorage.getItem("hk_vip_subscribers");
        if (saved) setSubscribers(JSON.parse(saved));
      } catch (e) {}
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const handleDeleteSubscriber = (subId) => {
    if (window.confirm("Remove this email from VIP subscriber list?")) {
      const updated = subscribers.filter(s => s.id !== subId);
      setSubscribers(updated);
      localStorage.setItem("hk_vip_subscribers", JSON.stringify(updated));
    }
  };

  const [saveSettingsMsg, setSaveSettingsMsg] = useState("");

  const handleSaveResortSettings = (e) => {
    e.preventDefault();
    const settings = {
      phone: resortPhone,
      whatsapp: resortWhatsapp,
      email: resortEmail,
      bookingEmail,
      techEmail,
      generalEmail,
      address: resortAddress,
      instagram: resortInstagram,
      facebook: resortFacebook,
      youtube: resortYoutube,
      locationUrl: resortLocationUrl,
      showInstagram,
      showFacebook,
      showYoutube,
      showLocation,
      showFloatingContact,
      showNewsletter,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem("hk_resort_settings", JSON.stringify(settings));
    window.dispatchEvent(new Event("storage"));
    setSaveSettingsMsg("✅ Resort contact, social media links & icon visibility settings updated live!");
    setTimeout(() => setSaveSettingsMsg(""), 4000);
  };

  // Secure redirect if not admin
  useEffect(() => {
    if (!isLoading && (!user || user.role !== "admin")) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  // ⚡ Real-time auto-refresh: poll every 5 seconds for live updates
  useEffect(() => {
    if (!user || user.role !== "admin") return;

    const interval = setInterval(() => {
      refreshBookings();
      refreshRooms();
      refreshCallbacks();
      refreshSupportQueries();
      refreshCoupons();
    }, 5000);

    return () => clearInterval(interval);
  }, [user, refreshBookings, refreshRooms, refreshCallbacks, refreshSupportQueries, refreshCoupons]);

  const handleStatusChange = async (bookingId, newStatus) => {
    setUpdatingId(bookingId);
    await adminUpdateStatus(bookingId, newStatus);
    setUpdatingId(null);
  };

  const handleCallbackStatusChange = async (callbackId, newStatus) => {
    setUpdatingId(callbackId);
    await adminUpdateCallbackStatus(callbackId, newStatus);
    setUpdatingId(null);
  };

  const handleSupportStatusChange = async (supportId, newStatus) => {
    setUpdatingId(supportId);
    await adminUpdateSupportStatus(supportId, newStatus);
    setUpdatingId(null);
  };

  const handleAddRoom = async (e) => {
    e.preventDefault();
    setAddRoomMsg("");
    setIsAddingRoom(true);
    const result = await adminAddRoom(newRoomType, parseFloat(newRoomPrice), parseInt(newRoomInventory));
    if (result.success) {
      setAddRoomMsg("✅ " + newRoomType + " added successfully!");
      setNewRoomType("");
      setNewRoomPrice("");
      setNewRoomInventory("");
      setTimeout(() => {
        setIsAddRoomModalOpen(false);
        setAddRoomMsg("");
      }, 1200);
    } else {
      setAddRoomMsg("❌ " + (result.error || "Failed to add room."));
    }
    setIsAddingRoom(false);
  };

  const handleToggleRoomStatus = async (room) => {
    const isFullNow = room.status === "Full" || (room.totalInventory !== undefined && room.bookedCount >= room.totalInventory);
    const newStatus = isFullNow ? "Available" : "Full";
    const typedVal = roomInputs[room.roomId];
    const totalVal = typedVal !== undefined && typedVal !== "" ? parseInt(typedVal) : (Number(room.totalInventory) ?? 10);

    setUpdatingId(room.roomId);
    await adminUpdateRoom(room.roomId, { status: newStatus, totalInventory: totalVal });
    setUpdatingId(null);
  };

  const handleUpdateRoomInventoryDirect = async (room, newTotalStr) => {
    setRoomInputs((prev) => ({ ...prev, [room.roomId]: newTotalStr }));
    const total = parseInt(newTotalStr);
    if (isNaN(total) || total < 0) return;

    const newStatus = room.bookedCount >= total ? "Full" : (room.status === "Full" ? "Available" : room.status || "Available");
    await adminUpdateRoom(room.roomId, { totalInventory: total, status: newStatus });
  };

  const handleResetBookedCount = async (room) => {
    setUpdatingId(room.roomId);
    await adminUpdateRoom(room.roomId, { bookedCount: 0, status: "Available" });
    setUpdatingId(null);
  };

  const handleOpenEditModal = (room) => {
    setEditingRoom(room);
    setEditRoomType(room.roomType);
    setEditRoomPrice(room.pricePerNight);
    setEditRoomInventory(room.totalInventory);
    setEditBookedCount(room.bookedCount);
    setEditRoomStatus(room.status);
    setEditDiscountPercent(room.discountPercent || 0);
    setEditPromoText(room.promoText || "");
    setEditRoomMsg("");
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreateAdminMsg("");

    if (newAdminPassword.length < 8) {
      setCreateAdminMsg("❌ Password must be at least 8 characters.");
      return;
    }
    if (newAdminPassword !== newAdminConfirm) {
      setCreateAdminMsg("❌ Passwords do not match.");
      return;
    }

    setIsCreatingAdmin(true);
    const result = await adminCreateAdmin(newAdminName, newAdminEmail, newAdminPhone, newAdminPassword);
    if (result.success) {
      setCreateAdminMsg("✅ Admin account created successfully!");
      setNewAdminName("");
      setNewAdminEmail("");
      setNewAdminPhone("");
      setNewAdminPassword("");
      setNewAdminConfirm("");
      setTimeout(() => {
        setIsAddAdminModalOpen(false);
        setCreateAdminMsg("");
      }, 1200);
    } else {
      setCreateAdminMsg("❌ " + (result.error || "Failed to create admin."));
    }
    setIsCreatingAdmin(false);
  };

  const handleSaveRoom = async (e) => {
    e.preventDefault();
    if (!editingRoom) return;

    setIsSavingRoom(true);
    setEditRoomMsg("");

    const updatedFields = {
      roomType: editRoomType,
      pricePerNight: parseFloat(editRoomPrice),
      totalInventory: parseInt(editRoomInventory),
      bookedCount: parseInt(editBookedCount),
      status: editRoomStatus,
      discountPercent: parseFloat(editDiscountPercent) || 0,
      promoText: editPromoText.trim()
    };

    const result = await adminUpdateRoom(editingRoom.roomId, updatedFields);
    if (result.success) {
      setEditRoomMsg("✅ Room details updated successfully!");
      setTimeout(() => {
        setEditingRoom(null);
      }, 1500);
    } else {
      setEditRoomMsg("❌ " + (result.error || "Failed to update room."));
    }
    setIsSavingRoom(false);
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setCreateCouponMsg("");

    if (!newCouponCode) {
      setCreateCouponMsg("❌ Coupon code is required.");
      return;
    }
    const val = parseFloat(newCouponValue);
    if (isNaN(val) || val <= 0) {
      setCreateCouponMsg("❌ Discount value must be a positive number.");
      return;
    }
    if (newCouponType === "percentage" && val > 100) {
      setCreateCouponMsg("❌ Percentage discount cannot exceed 100%.");
      return;
    }

    setIsCreatingCoupon(true);
    const result = await adminCreateCoupon(
      newCouponCode.toUpperCase().trim(),
      newCouponType,
      val,
      parseFloat(newCouponMinAmount) || 0,
      parseInt(newCouponMaxUses) || 0,
      newCouponValidFrom,
      newCouponValidTo
    );

    if (result.success) {
      setCreateCouponMsg("✅ Coupon '" + newCouponCode.toUpperCase().trim() + "' created successfully!");
      setNewCouponCode("");
      setNewCouponValue("");
      setNewCouponMinAmount("");
      setNewCouponMaxUses("");
      setNewCouponValidFrom("");
      setNewCouponValidTo("");
    } else {
      setCreateCouponMsg("❌ " + (result.error || "Failed to create coupon."));
    }
    setIsCreatingCoupon(false);
  };

  const handleCouponStatusChange = async (couponId, newStatus) => {
    setUpdatingId(couponId);
    await adminUpdateCouponStatus(couponId, newStatus);
    setUpdatingId(null);
  };

  const handleDeleteCoupon = async (couponId) => {
    if (window.confirm("Are you sure you want to delete this coupon? This action cannot be undone.")) {
      setUpdatingId(couponId);
      await adminDeleteCoupon(couponId);
      setUpdatingId(null);
    }
  };

  // Detail Modals State
  const [selectedBookingModal, setSelectedBookingModal] = useState(null);
  const [selectedCallbackModal, setSelectedCallbackModal] = useState(null);
  const [selectedQueryModal, setSelectedQueryModal] = useState(null);

  // Status Filter Pill States
  const [bookingStatusFilter, setBookingStatusFilter] = useState("ALL");
  const [callbackStatusFilter, setCallbackStatusFilter] = useState("ALL");
  const [queryStatusFilter, setQueryStatusFilter] = useState("ALL");

  // Priority order for sorting: Active/Pending on TOP (1), Resolved/Closed on BOTTOM (4-6)
  const getStatusPriority = (status) => {
    const s = (status || "Pending").toLowerCase().replace(/\s+/g, '');
    if (s === "pending") return 1;
    if (s === "inprogress") return 2;
    if (s === "confirmed") return 3;
    if (s === "resolved") return 4;
    if (s === "completed") return 5;
    if (s === "cancelled") return 6;
    return 10;
  };

  const filteredBookings = bookings
    .filter((b) => {
      const term = searchTerm.toLowerCase();
      const guest = b.guestName || "";
      const email = b.guestEmail || "";
      const phone = String(b.guestNumber || "");
      const matchesSearch = (
        guest.toLowerCase().includes(term) ||
        email.toLowerCase().includes(term) ||
        phone.includes(term) ||
        (b.roomType || "").toLowerCase().includes(term) ||
        (b.bookingId || "").toLowerCase().includes(term)
      );
      const bStatus = (b.status || "Pending").toLowerCase().replace(/\s+/g, '');
      const matchesStatus = bookingStatusFilter === "ALL" || bStatus === bookingStatusFilter.toLowerCase().replace(/\s+/g, '');
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => getStatusPriority(a.status) - getStatusPriority(b.status));

  const filteredCallbacks = callbacks
    .filter((c) => {
      const term = searchTerm.toLowerCase();
      const name = c.fullName || "";
      const matchesSearch = (
        name.toLowerCase().includes(term) ||
        (c.email || "").toLowerCase().includes(term) ||
        String(c.number || "").includes(term)
      );
      const cStatus = (c.status || "Pending").toLowerCase().replace(/\s+/g, '');
      const matchesStatus = callbackStatusFilter === "ALL" || cStatus === callbackStatusFilter.toLowerCase().replace(/\s+/g, '');
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => getStatusPriority(a.status) - getStatusPriority(b.status));

  const filteredSupportQueries = supportQueries
    .filter((q) => {
      const term = searchTerm.toLowerCase();
      const name = q.fullName || "";
      const msg = q.message || "";
      const matchesSearch = (
        name.toLowerCase().includes(term) ||
        (q.email || "").toLowerCase().includes(term) ||
        String(q.number || "").includes(term) ||
        msg.toLowerCase().includes(term)
      );
      const qStatus = (q.status || "Pending").toLowerCase().replace(/\s+/g, '');
      const matchesStatus = queryStatusFilter === "ALL" || qStatus === queryStatusFilter.toLowerCase().replace(/\s+/g, '');
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => getStatusPriority(a.status) - getStatusPriority(b.status));

  const filteredCoupons = coupons.filter((c) => {
    const term = searchTerm.toLowerCase();
    return (
      c.code.toLowerCase().includes(term) ||
      c.couponId.toLowerCase().includes(term) ||
      c.discountType.toLowerCase().includes(term)
    );
  });

  const isAdmin = user && (
    user.role?.toLowerCase() === "admin" || 
    user.email?.toLowerCase() === "admin@harikunjresort.com"
  );

  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <SkeletonLoader type="card" count={3} />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100 bg-light">
        <div className="text-center p-5 rounded-4 bg-white shadow border border-warning" style={{ maxWidth: "440px" }}>
          <i className="fa fa-lock text-warning fa-3x mb-3" />
          <h4 className="fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>Admin Access Required</h4>
          <p className="text-muted small mb-4">You must be logged in with an authorized Super Admin account to access this hub.</p>
          <button className="btn btn-warning rounded-pill px-4 py-2 fw-bold" onClick={() => navigate("/login")}>Go to Admin Login</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Resort Manager Hub – HariKunj Resort Jodhpur</title>
        <meta name="description" content="Secure administrative database panel for room reservations, room inventory, callback requests, and guest stay updates." />
      </Helmet>

      <div className="hk-dashboard-wrapper">
        
        {/* Sidebar Navigation */}
        <aside className={`hk-sidebar ${isMobileSidebarOpen ? "hk-sidebar--open" : ""}`}>
          <div className="hk-sidebar-brand">
            <span className="logo-main">HariKunj</span>
            <span className="logo-sub">Resort &bull; Admin Hub</span>
          </div>

          <div className="hk-sidebar-profile">
            <div className="profile-avatar">AD</div>
            <div className="profile-name">Resort Admin</div>
            <div className="profile-email">{user.email}</div>
          </div>

          <nav className="hk-sidebar-nav">
            <button 
              onClick={() => { setActiveTab("dashboard"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "dashboard" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-chart-line me-3" /> Dashboard Analytics
            </button>
            <button 
              onClick={() => { setActiveTab("bookings"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "bookings" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-calendar-check me-3" /> Bookings ({bookings.length})
            </button>
            <button 
              onClick={() => { setActiveTab("rooms"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "rooms" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-bed me-3" /> Room Inventory ({rooms.length})
            </button>
            <button 
              onClick={() => { setActiveTab("callbacks"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "callbacks" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-phone me-3" /> Callbacks ({callbacks.length})
            </button>
            <button 
              onClick={() => { setActiveTab("queries"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "queries" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-envelope me-3" /> Support Queries ({supportQueries.length})
            </button>
            <button 
              onClick={() => { setActiveTab("coupons"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "coupons" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-tags me-3" /> Manage Coupons ({coupons.length})
            </button>
            <button 
              onClick={() => { setActiveTab("admins"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "admins" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-user-shield me-3" /> Admin Management
            </button>
            <button 
              onClick={() => { setActiveTab("subscribers"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "subscribers" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-envelope-open me-3" /> VIP Subscribers ({subscribers.length})
            </button>
            <button 
              onClick={() => { setActiveTab("popupControls"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "popupControls" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-sliders-h me-3 text-warning" /> Booking Form Controls
            </button>
            <button 
              onClick={() => { setActiveTab("settings"); setSearchTerm(""); setIsMobileSidebarOpen(false); }}
              className={`nav-item ${activeTab === "settings" ? "nav-item--active" : ""}`}
            >
              <i className="fa fa-cog me-3" /> Resort Settings
            </button>
          </nav>

          <div className="hk-sidebar-footer">
            <button type="button" className="btn hk-btn-logout w-100" onClick={logout}>
              <i className="fa fa-sign-out-alt me-2" /> Log Out
            </button>
          </div>
        </aside>

        {/* Mobile Sidebar Overlay Backdrop */}
        {isMobileSidebarOpen && (
          <div 
            className="hk-sidebar-backdrop d-lg-none"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Main Content Area */}
        <main className="hk-main-content w-100">
          <div className="container-fluid py-3 px-2 px-lg-4">
            
            {/* Mobile Navigation Header Bar (Visible on screens < 992px) - Fixed Sticky on Scroll with 20px Rounded Corners */}
            <div
              className="d-flex d-lg-none align-items-center justify-content-between mb-3 p-3 shadow"
              style={{
                position: "sticky",
                top: "8px",
                zIndex: 1000,
                borderRadius: "20px",
                border: "1.5px solid rgba(212, 175, 55, 0.45)",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(12px)"
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <button 
                  type="button"
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className="btn btn-outline-dark rounded-circle d-flex align-items-center justify-content-center p-0"
                  style={{ width: "42px", height: "42px", border: "1.5px solid #0B1D37" }}
                  aria-label="Open Sidebar Menu"
                >
                  <i className="fa fa-bars fa-lg" style={{ color: "#0B1D37" }} />
                </button>
                <div>
                  <h5 className="m-0 fw-bold" style={{ fontFamily: "'Cinzel', serif", color: "#0B1D37", fontSize: "1.1rem" }}>
                    HARIKUNJ
                  </h5>
                  <span className="small text-warning fw-bold text-uppercase" style={{ fontSize: "0.65rem", letterSpacing: "0.05em" }}>
                    ADMIN HUB
                  </span>
                </div>
              </div>

              <button 
                onClick={() => { refreshBookings(); refreshRooms(); refreshCallbacks(); refreshSupportQueries(); refreshCoupons(); }}
                className="btn rounded-circle shadow-sm"
                title="Refresh Live Database"
                style={{
                  width: "40px",
                  height: "40px",
                  background: "#FFFFFF",
                  border: "1.5px solid rgba(212, 175, 55, 0.5)",
                  color: "#0B1D37",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer"
                }}
              >
                <i className={`fa fa-sync-alt ${isLoadingBookings || isLoadingRooms || isLoadingCallbacks || isLoadingSupportQueries || isLoadingCoupons ? "fa-spin text-warning" : ""}`} />
              </button>
            </div>

            {/* Desktop Header Bar with Title & Refresh Icon (Visible on desktop >= 992px) - Fixed Sticky on Scroll */}
            <div
              className="d-none d-lg-flex align-items-center justify-content-between mb-4 p-3 shadow-sm"
              style={{
                position: "sticky",
                top: "0",
                zIndex: 990,
                borderRadius: "16px",
                border: "1.5px solid rgba(212, 175, 55, 0.4)",
                background: "rgba(255, 255, 255, 0.94)",
                backdropFilter: "blur(12px)",
                boxShadow: "0 4px 20px rgba(11, 29, 55, 0.05)"
              }}
            >
              <div>
                <h3 className="m-0" style={{ fontFamily: "'Cinzel', serif", fontWeight: "700", color: "#0B1D37", fontSize: "1.45rem" }}>
                  {activeTab === "dashboard" && "Dashboard Analytics"}
                  {activeTab === "bookings" && `Guest Reservations (${bookings.length})`}
                  {activeTab === "rooms" && `Room Inventory & Pricing (${rooms.length})`}
                  {activeTab === "callbacks" && `Concierge Callback Requests (${callbacks.length})`}
                  {activeTab === "queries" && `Guest Support Queries (${supportQueries.length})`}
                  {activeTab === "coupons" && `Promotional Coupon Codes (${coupons.length})`}
                  {activeTab === "admins" && "Admin Access Control"}
                  {activeTab === "subscribers" && `VIP Newsletter Subscribers (${subscribers.length})`}
                  {activeTab === "popupControls" && "Booking Form Dropdown Controls"}
                  {activeTab === "settings" && "Resort Contact & Info Settings"}
                </h3>
                <span className="small text-muted" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Real-time live management portal &bull; HariKunj Resort Jodhpur
                </span>
              </div>

              <button 
                onClick={() => { refreshBookings(); refreshRooms(); refreshCallbacks(); refreshSupportQueries(); refreshCoupons(); }}
                className="btn rounded-circle shadow-sm"
                title="Refresh Live Database"
                style={{
                  width: "44px",
                  height: "44px",
                  background: "#FFFFFF",
                  border: "1.5px solid rgba(212, 175, 55, 0.45)",
                  color: "#0B1D37",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.25s ease"
                }}
              >
                <i className={`fa fa-sync-alt ${isLoadingBookings || isLoadingRooms || isLoadingCallbacks || isLoadingSupportQueries || isLoadingCoupons ? "fa-spin text-warning" : ""}`} style={{ fontSize: "1.15rem" }} />
              </button>
            </div>

            {/* Search Input Bar */}
            {activeTab !== "rooms" && activeTab !== "dashboard" && activeTab !== "settings" && (
              <div className="row mb-4">
                <div className="col-12 col-md-5">
                  <div className="input-group shadow-sm">
                    <span className="input-group-text bg-white border-end-0"><i className="fa fa-search text-muted" /></span>
                    <input
                      type="text"
                      placeholder="Search by name, email, phone, ID..."
                      className="form-control search-input border-start-0"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

        {/* ═══════════ DASHBOARD ANALYTICS TAB ═══════════ */}
        {activeTab === "dashboard" && (
          <div className="hk-dashboard-analytics-section">
            
            {/* Metric Cards Grid - 2 Per Row on Mobile View */}
            <div className="row g-2 g-md-3 mb-4">
              <div className="col-6 col-lg-3">
                <div className="p-3 rounded-4 bg-white shadow-sm d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between h-100" style={{ border: "1.5px solid rgba(212, 175, 55, 0.4)" }}>
                  <div>
                    <span className="text-muted small fw-bold text-uppercase d-block mb-1" style={{ fontSize: "0.68rem" }}>Reservations</span>
                    <h3 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif", fontSize: "1.4rem" }}>{bookings.length}</h3>
                    <span className="small text-success fw-semibold" style={{ fontSize: "0.72rem" }}><i className="fa fa-calendar-check me-1" />{bookings.filter(b => b.status === "Confirmed").length} Confirmed</span>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center mt-2 mt-sm-0" style={{ width: "42px", height: "42px", background: "rgba(254, 161, 22, 0.12)", color: "#FEA116", flexShrink: 0 }}>
                    <i className="fa fa-calendar-alt" />
                  </div>
                </div>
              </div>

              <div className="col-6 col-lg-3">
                <div className="p-3 rounded-4 bg-white shadow-sm d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between h-100" style={{ border: "1.5px solid rgba(212, 175, 55, 0.4)" }}>
                  <div>
                    <span className="text-muted small fw-bold text-uppercase d-block mb-1" style={{ fontSize: "0.68rem" }}>Est. Revenue</span>
                    <h3 className="m-0 fw-bold text-truncate" style={{ color: "#FEA116", fontFamily: "'Cinzel', serif", fontSize: "1.3rem" }}>
                      ₹{bookings.reduce((sum, b) => sum + (Number(b.totalCost) || 0), 0).toLocaleString("en-IN")}
                    </h3>
                    <span className="small text-muted fw-semibold" style={{ fontSize: "0.72rem" }}>All stays</span>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center mt-2 mt-sm-0" style={{ width: "42px", height: "42px", background: "rgba(46, 204, 113, 0.12)", color: "#2ecc71", flexShrink: 0 }}>
                    <i className="fa fa-rupee-sign" />
                  </div>
                </div>
              </div>

              <div className="col-6 col-lg-3">
                <div className="p-3 rounded-4 bg-white shadow-sm d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between h-100" style={{ border: "1.5px solid rgba(212, 175, 55, 0.4)" }}>
                  <div>
                    <span className="text-muted small fw-bold text-uppercase d-block mb-1" style={{ fontSize: "0.68rem" }}>Callbacks</span>
                    <h3 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif", fontSize: "1.4rem" }}>{callbacks.length}</h3>
                    <span className="small text-warning fw-semibold" style={{ fontSize: "0.72rem" }}><i className="fa fa-clock me-1" />{callbacks.filter(c => (c.status || "Pending") === "Pending").length} Action</span>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center mt-2 mt-sm-0" style={{ width: "42px", height: "42px", background: "rgba(240, 173, 78, 0.15)", color: "#f0ad4e", flexShrink: 0 }}>
                    <i className="fa fa-phone-volume" />
                  </div>
                </div>
              </div>

              <div className="col-6 col-lg-3">
                <div className="p-3 rounded-4 bg-white shadow-sm d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between h-100" style={{ border: "1.5px solid rgba(212, 175, 55, 0.4)" }}>
                  <div>
                    <span className="text-muted small fw-bold text-uppercase d-block mb-1" style={{ fontSize: "0.68rem" }}>Queries</span>
                    <h3 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif", fontSize: "1.4rem" }}>{supportQueries.length}</h3>
                    <span className="small text-info fw-semibold" style={{ fontSize: "0.72rem" }}><i className="fa fa-comments me-1" />{supportQueries.filter(q => (q.status || "Pending") === "Pending").length} Open</span>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center mt-2 mt-sm-0" style={{ width: "42px", height: "42px", background: "rgba(23, 162, 184, 0.12)", color: "#17a2b8", flexShrink: 0 }}>
                    <i className="fa fa-envelope-open-text" />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Analytics Grid */}
            <div className="row g-4 mb-4">
              {/* Room Inventory Overview */}
              <div className="col-12 col-lg-6">
                <div className="p-4 rounded-4 bg-white shadow-sm h-100" style={{ border: "1.5px solid rgba(212, 175, 55, 0.4)" }}>
                  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <h5 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}><i className="fa fa-bed text-warning me-2" />Room Availability Status</h5>
                    <button className="btn btn-sm btn-outline-dark rounded-pill fw-semibold" onClick={() => setActiveTab("rooms")}>Manage Rooms</button>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {rooms.map((room, idx) => {
                      const totalInv = Number(room.totalInventory) || 0;
                      const bookedCnt = Number(room.bookedCount) || 0;
                      const avail = Math.max(0, totalInv - bookedCnt);
                      const pct = totalInv > 0 ? Math.round((bookedCnt / totalInv) * 100) : 0;
                      return (
                        <div key={room.roomId || room.id || `rm-dash-${idx}`} className="p-3 rounded-3" style={{ background: "#FAF6F0", border: "1px solid rgba(212, 175, 55, 0.3)" }}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fw-bold" style={{ color: "#0B1D37" }}>{room.roomType}</span>
                            <span className="badge bg-gold">₹{room.pricePerNight} / night</span>
                          </div>
                          <div className="progress mb-1" style={{ height: "8px", borderRadius: "4px" }}>
                            <div className={`progress-bar ${pct >= 90 ? "bg-danger" : pct >= 60 ? "bg-warning" : "bg-success"}`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="d-flex justify-content-between small text-muted">
                            <span>{bookedCnt} booked</span>
                            <span className="fw-semibold" style={{ color: avail === 0 ? "#dc3545" : "#28a745" }}>{avail} available of {totalInv}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Recent Concierge Callbacks */}
              <div className="col-12 col-lg-6">
                <div className="p-4 rounded-4 bg-white shadow-sm h-100" style={{ border: "1.5px solid rgba(212, 175, 55, 0.4)" }}>
                  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                    <h5 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}><i className="fa fa-phone-volume text-warning me-2" />Recent Concierge Callbacks</h5>
                    <button className="btn btn-sm btn-outline-dark rounded-pill fw-semibold" onClick={() => setActiveTab("callbacks")}>View All ({callbacks.length})</button>
                  </div>
                  {callbacks.length > 0 ? (
                    <div className="d-flex flex-column gap-2">
                      {callbacks.slice(0, 4).map((cb, idx) => (
                        <div key={cb.id || `cb-dash-${idx}`} className="p-3 rounded-3 d-flex align-items-center justify-content-between" style={{ background: "#FAF6F0", border: "1px solid rgba(212, 175, 55, 0.3)" }}>
                          <div>
                            <strong style={{ color: "#0B1D37", display: "block" }}>{cb.fullName || "Guest"}</strong>
                            <span className="small" style={{ color: "#1E3E72" }}><i className="fa fa-phone me-1 text-success" />{cb.number}</span>
                          </div>
                          <span className={`badge ${cb.status === 'Resolved' ? 'bg-success' : 'bg-warning text-dark'}`}>
                            {cb.status || "Pending"}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      <i className="fa fa-phone-slash fa-2x mb-2 d-block text-warning" />
                      No recent callback requests.
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        )}

        {/* ═══════════ ROOM MANAGEMENT TAB ═══════════ */}
        {activeTab === "rooms" && (
          <div className="rooms-management-section">
            
            {/* Top Bar with Add New Room Trigger Button */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4 p-3 rounded-4 bg-white shadow-sm border border-warning">
              <div>
                <h5 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>
                  <i className="fa fa-bed text-warning me-2" />Room Categories & Inventory
                </h5>
                <span className="small text-muted font-poppins">Manage room tariffs, availability status, and inventory allocations.</span>
              </div>
              <button
                className="btn btn-action-gold rounded-pill px-4 fw-bold shadow-sm"
                onClick={() => { setAddRoomMsg(""); setIsAddRoomModalOpen(true); }}
              >
                <i className="fa fa-plus-circle me-2" />Add New Room
              </button>
            </div>

            {/* Room Inventory Cards */}
            <div className="row g-4">
              {rooms.length > 0 ? rooms.map((room) => {
                const totalInv = Number(room.totalInventory) || 0;
                const bookedCnt = Number(room.bookedCount) || 0;
                const available = Math.max(0, totalInv - bookedCnt);
                const occupancyPct = totalInv > 0 ? Math.round((bookedCnt / totalInv) * 100) : 0;
                const isFull = room.status === "Full" || available === 0;

                return (
                  <div className="col-12 col-md-6 col-lg-4" key={room.roomId}>
                    <div className={`rm-card ${isFull ? "rm-card--full" : "rm-card--available"}`}>
                      
                      {/* Status Badge */}
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                          <h5 className="rm-room-name">{room.roomType}</h5>
                          <code className="rm-room-id">{room.roomId}</code>
                        </div>
                        <div className="d-flex align-items-center">
                          <span className={`rm-status-badge ${isFull ? "rm-status--full" : "rm-status--available"}`}>
                            {isFull ? "FULL" : "AVAILABLE"}
                          </span>
                          <button
                            onClick={() => handleOpenEditModal(room)}
                            className="btn btn-sm btn-outline-warning rounded-circle ms-2"
                            style={{ width: "28px", height: "28px", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                            title="Edit Room"
                          >
                            <i className="fa fa-pencil-alt" style={{fontSize: "0.75rem"}} />
                          </button>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="rm-price mb-3">
                        <span className="rm-price-value">₹{room.pricePerNight}</span>
                        <span className="rm-price-unit">/night</span>
                      </div>

                      {/* Occupancy Bar */}
                      <div className="rm-occupancy mb-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="fw-bold text-muted">OCCUPANCY</small>
                          <small className="fw-bold">{occupancyPct}%</small>
                        </div>
                        <div className="rm-progress-bar">
                          <div
                            className={`rm-progress-fill ${occupancyPct >= 90 ? "rm-fill--danger" : occupancyPct >= 60 ? "rm-fill--warning" : "rm-fill--good"}`}
                            style={{ width: `${occupancyPct}%` }}
                          />
                        </div>
                        <div className="d-flex justify-content-between mt-1">
                          <small className="text-muted">{room.bookedCount} booked</small>
                          <small className="text-muted">{available} available / {room.totalInventory} total</small>
                        </div>
                      </div>

                      {/* Inventory Controls */}
                      <div className="rm-controls">
                        <div className="d-flex gap-2 mb-2">
                          <label className="form-label text-muted small fw-bold mb-0 align-self-center" style={{minWidth: "85px"}}>TOTAL ROOMS</label>
                          <input
                            type="number"
                            className="form-control form-control-sm rm-input"
                            value={roomInputs[room.roomId] !== undefined ? roomInputs[room.roomId] : (room.totalInventory ?? 10)}
                            min="0"
                            onChange={(e) => handleUpdateRoomInventoryDirect(room, e.target.value)}
                          />
                        </div>

                        <div className="d-flex gap-2">
                          <button
                            className={`btn btn-sm flex-fill rounded-pill fw-bold ${isFull ? "btn-outline-success" : "btn-outline-danger"}`}
                            onClick={() => handleToggleRoomStatus(room)}
                            disabled={updatingId === room.roomId}
                          >
                            {updatingId === room.roomId ? (
                              <i className="fa fa-spinner fa-spin" />
                            ) : isFull ? (
                              <><i className="fa fa-check-circle me-1" />Mark Available</>
                            ) : (
                              <><i className="fa fa-ban me-1" />Mark Full</>
                            )}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-warning rounded-pill fw-bold"
                            onClick={() => handleResetBookedCount(room)}
                            disabled={updatingId === room.roomId}
                            title="Reset booked count to 0"
                          >
                            <i className="fa fa-redo me-1" />Reset
                          </button>
                        </div>
                      </div>

                      {/* Last Updated */}
                      {room.lastUpdated && (
                        <div className="mt-3 text-end">
                          <small className="text-muted" style={{fontSize: "0.7rem"}}>
                            Updated: {new Date(room.lastUpdated).toLocaleString()}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }) : (
                <div className="col-12 text-center py-5">
                  <i className="fa fa-bed fa-3x text-muted mb-3 d-block" />
                  <h5 className="text-muted">No rooms configured yet</h5>
                  <p className="text-muted">Use the form above to add your first room type.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════ BOOKINGS TAB ═══════════ */}
        {activeTab === "bookings" && (
          <div>
            {/* Smart Filter Pills */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="small text-muted fw-bold me-2"><i className="fa fa-filter text-warning me-1" />FILTER:</span>
              {[
                { label: "All Reservations", value: "ALL", count: bookings.length },
                { label: "⏳ Pending", value: "Pending", count: bookings.filter(b => (b.status||"Pending").toLowerCase()==="pending").length },
                { label: "✅ Confirmed", value: "Confirmed", count: bookings.filter(b => (b.status||"Pending").toLowerCase()==="confirmed").length },
                { label: "🏁 Completed", value: "Completed", count: bookings.filter(b => (b.status||"Pending").toLowerCase()==="completed").length },
                { label: "❌ Cancelled", value: "Cancelled", count: bookings.filter(b => (b.status||"Pending").toLowerCase()==="cancelled").length },
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setBookingStatusFilter(f.value)}
                  className={`btn btn-sm rounded-pill font-poppins fw-bold px-3 py-1 ${bookingStatusFilter === f.value ? "btn-dark text-warning border-warning shadow-sm" : "btn-outline-secondary bg-white"}`}
                  style={{ fontSize: "0.78rem" }}
                >
                  {f.label} ({f.count})
                </button>
              ))}
            </div>

            <div className="luxury-table-card rounded shadow">
              <div className="table-responsive">
                <table className="hk-luxury-table mb-0">
                  <thead>
                    <tr>
                      <th>Booking ID</th>
                      <th>Guest Details</th>
                      <th>Stay Window</th>
                      <th>Room Configuration</th>
                      <th>Total Tariff</th>
                      <th>Actions & Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.length > 0 ? (
                      filteredBookings.map((b) => (
                        <tr key={b.bookingId}>
                          <td data-label="Booking ID">
                            <code className="font-monospace fw-bold" style={{ color: "#E38A12" }}>{b.bookingId}</code>
                          </td>
                          <td data-label="Guest Details">
                            <div className="d-flex flex-column">
                              <strong style={{ color: "#0B1D37" }}>{b.guestName || "Guest"}</strong>
                              <span className="small text-muted">{b.guestEmail}</span>
                              <span className="small text-muted">{b.guestNumber}</span>
                            </div>
                          </td>
                          <td data-label="Stay Window">
                            <div className="d-flex flex-column">
                              <span className="fw-semibold text-dark">{b.checkIn} ➔ {b.checkOut}</span>
                              <span className="small text-muted">Booked: {b.createdAt ? new Date(b.createdAt).toLocaleDateString() : "-"}</span>
                            </div>
                          </td>
                          <td data-label="Room Config">
                            <div className="d-flex flex-column">
                              <span className="badge bg-gold align-self-start mb-1">{b.roomType}</span>
                              <span className="small text-muted">{b.adults} Adults, {b.children} Children</span>
                            </div>
                          </td>
                          <td data-label="Total Tariff">
                            <strong className="text-warning">₹{Number(b.totalCost).toLocaleString("en-IN")}</strong>
                          </td>
                          <td data-label="Actions & Status">
                            <div className="d-flex align-items-center gap-2 justify-content-end">
                              <button
                                className="btn btn-sm btn-outline-dark rounded-pill fw-bold"
                                style={{ fontSize: "0.75rem" }}
                                onClick={() => setSelectedBookingModal(b)}
                              >
                                <i className="fa fa-eye me-1 text-warning" /> Details
                              </button>

                              <HKStatusDropdown
                                value={b.status || "Pending"}
                                options={BOOKING_STATUS_OPTIONS}
                                onChange={(newVal) => handleStatusChange(b.bookingId, newVal)}
                                disabled={updatingId === b.bookingId}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">
                          No bookings found matching query.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ CALLBACKS TAB ═══════════ */}
        {activeTab === "callbacks" && (
          <div>
            {/* Smart Filter Pills */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="small text-muted fw-bold me-2"><i className="fa fa-filter text-warning me-1" />FILTER:</span>
              {[
                { label: "All Inquiries", value: "ALL", count: callbacks.length },
                { label: "⏳ Pending", value: "Pending", count: callbacks.filter(c => (c.status||"Pending").toLowerCase()==="pending").length },
                { label: "🔄 In Progress", value: "In Progress", count: callbacks.filter(c => (c.status||"Pending").toLowerCase().replace(/\s+/g,'')==="inprogress").length },
                { label: "✅ Resolved", value: "Resolved", count: callbacks.filter(c => (c.status||"Pending").toLowerCase()==="resolved").length },
                { label: "❌ Cancelled", value: "Cancelled", count: callbacks.filter(c => (c.status||"Pending").toLowerCase()==="cancelled").length },
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setCallbackStatusFilter(f.value)}
                  className={`btn btn-sm rounded-pill font-poppins fw-bold px-3 py-1 ${callbackStatusFilter === f.value ? "btn-dark text-warning border-warning shadow-sm" : "btn-outline-secondary bg-white"}`}
                  style={{ fontSize: "0.78rem" }}
                >
                  {f.label} ({f.count})
                </button>
              ))}
            </div>

            <div className="luxury-table-card rounded shadow">
              <div className="table-responsive">
                <table className="hk-luxury-table mb-0">
                  <thead>
                    <tr>
                      <th>Guest</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Date Logged</th>
                      <th>Actions & Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCallbacks.length > 0 ? (
                      filteredCallbacks.map((c, idx) => (
                        <tr key={c.id || `cb-${idx}`}>
                          <td data-label="Guest"><strong style={{ color: "#0B1D37" }}>{c.fullName || "-"}</strong></td>
                          <td data-label="Email">
                            <a href={`mailto:${c.email}`} style={{ color: "#1E3E72", textDecoration: "none", fontWeight: "600" }}>
                              <i className="fa fa-envelope me-1 text-warning" /> {c.email}
                            </a>
                          </td>
                          <td data-label="Phone">
                            <a href={`tel:${c.number}`} style={{ color: "#1E3E72", textDecoration: "none", fontWeight: "600" }}>
                              <i className="fa fa-phone me-1 text-success" /> {c.number}
                            </a>
                          </td>
                          <td data-label="Date Logged" style={{ color: "#0B1D37" }}>{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : "-"}</td>
                          <td data-label="Actions & Status">
                            <div className="d-flex align-items-center gap-2 justify-content-end">
                              <button
                                className="btn btn-sm btn-outline-dark rounded-pill fw-bold"
                                style={{ fontSize: "0.75rem" }}
                                onClick={() => setSelectedCallbackModal(c)}
                              >
                                <i className="fa fa-eye me-1 text-warning" /> Details
                              </button>

                              <HKStatusDropdown
                                value={c.status || "Pending"}
                                options={GENERAL_STATUS_OPTIONS}
                                onChange={(newVal) => handleCallbackStatusChange(c.id, newVal)}
                                disabled={updatingId === c.id}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-5 text-muted">
                          No contact inquiries found matching query.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ SUPPORT QUERIES TAB ═══════════ */}
        {activeTab === "queries" && (
          <div>
            {/* Smart Filter Pills */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <span className="small text-muted fw-bold me-2"><i className="fa fa-filter text-warning me-1" />FILTER:</span>
              {[
                { label: "All Support Queries", value: "ALL", count: supportQueries.length },
                { label: "⏳ Pending", value: "Pending", count: supportQueries.filter(q => (q.status||"Pending").toLowerCase()==="pending").length },
                { label: "🔄 In Progress", value: "In Progress", count: supportQueries.filter(q => (q.status||"Pending").toLowerCase().replace(/\s+/g,'')==="inprogress").length },
                { label: "✅ Resolved", value: "Resolved", count: supportQueries.filter(q => (q.status||"Pending").toLowerCase()==="resolved").length },
                { label: "❌ Cancelled", value: "Cancelled", count: supportQueries.filter(q => (q.status||"Pending").toLowerCase()==="cancelled").length },
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setQueryStatusFilter(f.value)}
                  className={`btn btn-sm rounded-pill font-poppins fw-bold px-3 py-1 ${queryStatusFilter === f.value ? "btn-dark text-warning border-warning shadow-sm" : "btn-outline-secondary bg-white"}`}
                  style={{ fontSize: "0.78rem" }}
                >
                  {f.label} ({f.count})
                </button>
              ))}
            </div>

            <div className="luxury-table-card rounded shadow">
              <div className="table-responsive">
                <table className="hk-luxury-table mb-0">
                  <thead>
                    <tr>
                      <th>Guest</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Message</th>
                      <th>Date Logged</th>
                      <th>Actions & Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSupportQueries.length > 0 ? (
                      filteredSupportQueries.map((q, idx) => (
                        <tr key={q.id || `sq-${idx}`}>
                          <td data-label="Guest"><strong style={{ color: "#0B1D37" }}>{q.fullName || "-"}</strong></td>
                          <td data-label="Email">
                            <a href={`mailto:${q.email}`} style={{ color: "#1E3E72", textDecoration: "none", fontWeight: "600" }}>
                              <i className="fa fa-envelope me-1 text-warning" /> {q.email}
                            </a>
                          </td>
                          <td data-label="Phone">
                            <a href={`tel:${q.number}`} style={{ color: "#1E3E72", textDecoration: "none", fontWeight: "600" }}>
                              <i className="fa fa-phone me-1 text-success" /> {q.number}
                            </a>
                          </td>
                          <td data-label="Message">
                            <div className="hk-query-message" style={{ maxWidth: "240px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#0B1D37" }}>
                              {q.message}
                            </div>
                          </td>
                          <td data-label="Date Logged" style={{ color: "#0B1D37" }}>{q.timestamp ? new Date(q.timestamp).toLocaleDateString() : "-"}</td>
                          <td data-label="Actions & Status">
                            <div className="d-flex align-items-center gap-2 justify-content-end">
                              <button
                                className="btn btn-sm btn-outline-dark rounded-pill fw-bold"
                                style={{ fontSize: "0.75rem" }}
                                onClick={() => setSelectedQueryModal(q)}
                              >
                                <i className="fa fa-eye me-1 text-warning" /> Details
                              </button>

                              <HKStatusDropdown
                                value={q.status || "Pending"}
                                options={GENERAL_STATUS_OPTIONS}
                                onChange={(newVal) => handleSupportStatusChange(q.id, newVal)}
                                disabled={updatingId === q.id}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-5 text-muted">
                          No support inquiries found matching search query.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ COUPONS TAB ═══════════ */}
        {activeTab === "coupons" && (
          <div className="admin-mgmt-section">
            <div className="row g-4">
              {/* Create New Coupon Card */}
              <div className="col-12 col-lg-5">
                <div className="add-room-card p-4 rounded-4 shadow-sm">
                  <h5 className="rm-section-title mb-1">
                    <i className="fa fa-tag text-warning me-2" />Create New Coupon Code
                  </h5>
                  <p className="text-muted small mb-4">Create a promotional code to attract customers. Coupons can offer flat value or percentage discounts.</p>
                  
                  <form onSubmit={handleCreateCoupon}>
                    <div className="mb-3">
                      <label className="form-label text-muted small fw-bold">COUPON CODE (3-20 Alphanumeric)</label>
                      <div className="input-group">
                        <span className="input-group-text bg-white"><i className="fa fa-tags text-warning" /></span>
                        <input
                          type="text"
                          className="form-control rm-input"
                          placeholder="e.g. WELCOME10"
                          value={newCouponCode}
                          onChange={(e) => setNewCouponCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/gi, ""))}
                          required
                          maxLength={20}
                        />
                      </div>
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">DISCOUNT TYPE</label>
                        <select
                          className="form-select rm-input"
                          value={newCouponType}
                          onChange={(e) => setNewCouponType(e.target.value)}
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="flat">Flat Value (₹)</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">DISCOUNT VALUE</label>
                        <input
                          type="number"
                          className="form-control rm-input"
                          placeholder={newCouponType === "percentage" ? "e.g. 10 (for 10%)" : "e.g. 500 (for ₹500)"}
                          value={newCouponValue}
                          onChange={(e) => setNewCouponValue(e.target.value)}
                          min="1"
                          required
                        />
                      </div>
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">MIN BOOKING AMOUNT (₹)</label>
                        <input
                          type="number"
                          className="form-control rm-input"
                          placeholder="e.g. 2000 (0 for none)"
                          value={newCouponMinAmount}
                          onChange={(e) => setNewCouponMinAmount(e.target.value)}
                          min="0"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">MAX USES LIMIT</label>
                        <input
                          type="number"
                          className="form-control rm-input"
                          placeholder="e.g. 100 (0 for unlimited)"
                          value={newCouponMaxUses}
                          onChange={(e) => setNewCouponMaxUses(e.target.value)}
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="row g-3 mb-4">
                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">VALID FROM DATE</label>
                        <input
                          type="date"
                          className="form-control rm-input"
                          value={newCouponValidFrom}
                          onChange={(e) => setNewCouponValidFrom(e.target.value)}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">VALID TO DATE</label>
                        <input
                          type="date"
                          className="form-control rm-input"
                          value={newCouponValidTo}
                          onChange={(e) => setNewCouponValidTo(e.target.value)}
                        />
                      </div>
                    </div>

                    <button type="submit" className="btn btn-action-gold w-100 rounded-pill py-2" disabled={isCreatingCoupon}>
                      {isCreatingCoupon ? <><i className="fa fa-spinner fa-spin me-2" />Creating Coupon...</> : <><i className="fa fa-plus-circle me-2" />Create Coupon</>}
                    </button>

                    {createCouponMsg && (
                      <div className="mt-3 text-center small fw-bold" style={{ color: createCouponMsg.startsWith("❌") ? "#ff4d4d" : "#2ecc71" }}>
                        {createCouponMsg}
                      </div>
                    )}
                  </form>
                </div>
              </div>

              {/* Coupons List Database */}
              <div className="col-12 col-lg-7">
                <div className="luxury-table-card rounded shadow">
                  <div className="table-responsive">
                    <table className="hk-luxury-table mb-0">
                      <thead>
                        <tr>
                          <th>Code Details</th>
                          <th>Discount</th>
                          <th>Limits / Usage</th>
                          <th>Validity Period</th>
                          <th>Status / Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCoupons.length > 0 ? (
                          filteredCoupons.map((c) => (
                            <tr key={c.couponId}>
                              <td>
                                <div className="fw-bold text-warning">{c.code}</div>
                                <div className="text-muted small" style={{ fontSize: "0.75rem" }}>ID: {c.couponId}</div>
                                {c.minBookingAmount > 0 && (
                                  <span className="badge bg-secondary mt-1" style={{ fontSize: "0.7rem" }}>
                                    Min Book: ₹{c.minBookingAmount}
                                  </span>
                                )}
                              </td>
                              <td>
                                <div className="fw-bold">
                                  {c.discountType === "percentage" ? `${c.discountValue}% Off` : `₹${c.discountValue} Off`}
                                </div>
                                <span className="text-muted small" style={{ fontSize: "0.75rem" }}>
                                  {c.discountType === "percentage" ? "Percentage" : "Flat Value"}
                                </span>
                              </td>
                              <td>
                                <div className="small">
                                  Uses: <strong>{c.usedCount}</strong> / {c.maxUses > 0 ? c.maxUses : "∞"}
                                </div>
                                <div className="progress mt-1" style={{ height: "4px", backgroundColor: "#333", width: "100px" }}>
                                  <div 
                                    className="progress-bar bg-warning" 
                                    style={{ 
                                      width: c.maxUses > 0 ? `${(c.usedCount / c.maxUses) * 100}%` : "10%" 
                                    }}
                                  />
                                </div>
                              </td>
                              <td>
                                <div className="small text-light opacity-75">
                                  From: {c.validFrom ? new Date(c.validFrom).toLocaleDateString("en-IN") : "Immediate"}
                                </div>
                                <div className="small text-light opacity-75">
                                  To: {c.validTo ? new Date(c.validTo).toLocaleDateString("en-IN") : "Indefinite"}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="position-relative">
                                    {updatingId === c.couponId && (
                                      <span className="spinner-border spinner-border-sm text-warning position-absolute start-0 translate-middle-y" style={{ top: "50%" }} />
                                    )}
                                    <select
                                      value={c.status}
                                      onChange={(e) => handleCouponStatusChange(c.couponId, e.target.value)}
                                      className={`form-select form-select-sm border-secondary text-white rounded px-2 py-1 ${
                                        c.status === "Active" ? "bg-success" : c.status === "Expired" ? "bg-warning text-dark" : "bg-danger"
                                      }`}
                                      style={{ fontSize: "0.8rem", cursor: "pointer", width: "100px" }}
                                      disabled={updatingId === c.couponId}
                                    >
                                      <option value="Active">🟢 Active</option>
                                      <option value="Disabled">🔴 Disabled</option>
                                      <option value="Expired">🟡 Expired</option>
                                    </select>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteCoupon(c.couponId)}
                                    className="btn btn-outline-danger btn-sm p-1 rounded"
                                    title="Delete Coupon"
                                    disabled={updatingId === c.couponId}
                                  >
                                    <i className="fa fa-trash-alt" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center py-5 text-muted">
                              No promotional coupons found matching search query.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ ADMIN MANAGEMENT TAB ═══════════ */}
        {activeTab === "admins" && (
          <div className="admin-mgmt-section">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4 p-3 rounded-4 bg-white shadow-sm border border-warning">
              <div>
                <h5 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>
                  <i className="fa fa-user-shield text-warning me-2" />Admin Privilege Management
                </h5>
                <span className="small text-muted font-poppins">Manage authorized resort managers and security credentials.</span>
              </div>
              <button
                className="btn btn-action-gold rounded-pill px-4 fw-bold shadow-sm"
                onClick={() => { setCreateAdminMsg(""); setIsAddAdminModalOpen(true); }}
              >
                <i className="fa fa-user-plus me-2" />Create New Admin
              </button>
            </div>

            <div className="row g-4 justify-content-center">
              {/* Admin Security Info Card */}
              <div className="col-12 col-lg-10">
                <div className="add-room-card p-4 rounded-4 shadow-sm h-100 d-flex flex-column">
                  <h5 className="rm-section-title mb-3">
                    <i className="fa fa-shield-alt text-warning me-2" />Security Information
                  </h5>
                  <div className="d-flex flex-column gap-3 flex-grow-1">
                    <div className="admin-info-item p-3 rounded-3" style={{ background: "rgba(254, 161, 22, 0.06)", border: "1px solid rgba(254, 161, 22, 0.2)" }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className="fa fa-check-circle text-success" />
                        <strong className="small">HMAC-SHA256 Signed Requests</strong>
                      </div>
                      <p className="text-muted small mb-0">All admin actions are cryptographically signed to prevent tampering.</p>
                    </div>
                    <div className="admin-info-item p-3 rounded-3" style={{ background: "rgba(254, 161, 22, 0.06)", border: "1px solid rgba(254, 161, 22, 0.2)" }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className="fa fa-check-circle text-success" />
                        <strong className="small">SHA-256 Salted Password Hashing</strong>
                      </div>
                      <p className="text-muted small mb-0">Passwords are never stored in plain text. Each account uses a unique salt.</p>
                    </div>
                    <div className="admin-info-item p-3 rounded-3" style={{ background: "rgba(254, 161, 22, 0.06)", border: "1px solid rgba(254, 161, 22, 0.2)" }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className="fa fa-check-circle text-success" />
                        <strong className="small">Role-Based Access Control</strong>
                      </div>
                      <p className="text-muted small mb-0">Admin privileges are enforced server-side. Only admins can create other admins.</p>
                    </div>
                    <div className="admin-info-item p-3 rounded-3" style={{ background: "rgba(46, 204, 113, 0.06)", border: "1px solid rgba(46, 204, 113, 0.2)" }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className="fa fa-info-circle text-info" />
                        <strong className="small">Current Session</strong>
                      </div>
                      <p className="text-muted small mb-0">Logged in as: <strong className="text-dark">{user.email}</strong></p>
                    </div>
                    <div className="admin-info-item p-3 rounded-3" style={{ background: "rgba(212, 175, 55, 0.08)", border: "1.5px solid rgba(212, 175, 55, 0.35)" }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className="fa fa-crown" style={{ color: "#d4af37" }} />
                        <strong className="small" style={{ color: "#d4af37" }}>Super Admin (Protected)</strong>
                      </div>
                      <p className="text-muted small mb-2">
                        <code className="text-warning" style={{ fontSize: "0.82rem" }}>admin@harikunjresort.com</code>
                      </p>
                      <p className="text-muted small mb-0" style={{ lineHeight: 1.4 }}>
                        This is the primary super admin account. It <strong>cannot be deleted, demoted, or overwritten</strong> by any other admin. This account is auto-provisioned on every deploy.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ VIP SUBSCRIBERS TAB ═══════════ */}
        {activeTab === "subscribers" && (
          <div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <span className="small text-muted font-poppins">
                <i className="fa fa-crown text-warning me-1" /> Total Registered VIP Subscribers: <strong>{subscribers.length}</strong>
              </span>
            </div>

            <div className="luxury-table-card rounded-4 shadow">
              <div className="table-responsive">
                <table className="hk-luxury-table mb-0">
                  <thead>
                    <tr>
                      <th>Subscriber ID</th>
                      <th>Email Address</th>
                      <th>Subscription Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.length > 0 ? (
                      subscribers.map((sub, idx) => (
                        <tr key={sub.id || `sub-${idx}`}>
                          <td data-label="Subscriber ID"><code className="font-monospace fw-bold" style={{ color: "#E38A12" }}>{sub.id}</code></td>
                          <td data-label="Email Address">
                            <a href={`mailto:${sub.email}`} style={{ color: "#1E3E72", textDecoration: "none", fontWeight: "600" }}>
                              <i className="fa fa-envelope me-2 text-warning" /> {sub.email}
                            </a>
                          </td>
                          <td data-label="Subscription Date" style={{ color: "#0B1D37" }}>
                            {sub.subscribedAt ? new Date(sub.subscribedAt).toLocaleString() : "-"}
                          </td>
                          <td data-label="Action">
                            <button
                              className="btn btn-sm btn-outline-danger rounded-circle"
                              title="Delete Subscriber"
                              style={{ width: "32px", height: "32px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                              onClick={() => handleDeleteSubscriber(sub.id)}
                            >
                              <i className="fa fa-trash-alt" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-5 text-muted">
                          No VIP subscribers found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ BOOKING POPUP DROPDOWN CONTROLS TAB ═══════════ */}
        {activeTab === "popupControls" && (
          <div className="admin-mgmt-section">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4 p-3 rounded-4 bg-white shadow-sm border border-warning">
              <div>
                <h5 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>
                  <i className="fa fa-sliders-h text-warning me-2" />Booking Form Dropdown Controls
                </h5>
                <span className="small text-muted font-poppins">Manage live dropdown choices for Adults and Children in website reservation popup.</span>
              </div>
              <button
                className="btn btn-outline-dark rounded-pill px-4 fw-bold shadow-sm"
                onClick={handleResetPopupOptions}
              >
                <i className="fa fa-undo me-2 text-warning" />Reset to Defaults
              </button>
            </div>

            {popupCtrlMsg && (
              <div className="alert alert-success fw-bold text-center mb-4 rounded-4 shadow-sm">
                {popupCtrlMsg}
              </div>
            )}

            <div className="row g-4">
              {/* Adults Dropdown Management */}
              <div className="col-12 col-lg-6">
                <div className="p-4 rounded-4 bg-white shadow-sm h-100 border border-warning">
                  <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
                    <h5 className="m-0 fw-bold" style={{ color: "#0B1D37" }}>
                      <i className="fa fa-users text-warning me-2" />Adults Dropdown Options
                    </h5>
                    <span className="badge bg-gold text-dark fw-bold">{adultOptions.length} Options</span>
                  </div>

                  <form onSubmit={handleAddAdultOption} className="d-flex gap-2 mb-4">
                    <input
                      type="text"
                      className="form-control rm-input"
                      placeholder="e.g. Adult 6+ (VIP Villa)"
                      value={newAdultInput}
                      onChange={(e) => setNewAdultInput(e.target.value)}
                      required
                    />
                    <button type="submit" className="btn btn-action-gold rounded-pill px-3 fw-bold flex-shrink-0">
                      <i className="fa fa-plus me-1" /> Add Option
                    </button>
                  </form>

                  <div className="d-flex flex-column gap-2">
                    {adultOptions.map((opt, idx) => (
                      <div
                        key={`adult-opt-${idx}`}
                        className="d-flex align-items-center justify-content-between p-3 rounded-3"
                        style={{ background: "#FAF6F0", border: "1px solid rgba(212, 175, 55, 0.3)" }}
                      >
                        <span className="fw-semibold text-dark"><i className="fa fa-check text-success me-2" />{opt}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger rounded-circle"
                          title="Remove Option"
                          style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}
                          onClick={() => handleRemoveAdultOption(idx)}
                          disabled={adultOptions.length <= 1}
                        >
                          <i className="fa fa-trash-alt" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Children Dropdown Management */}
              <div className="col-12 col-lg-6">
                <div className="p-4 rounded-4 bg-white shadow-sm h-100 border border-warning">
                  <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
                    <h5 className="m-0 fw-bold" style={{ color: "#0B1D37" }}>
                      <i className="fa fa-child text-warning me-2" />Children Dropdown Options
                    </h5>
                    <span className="badge bg-gold text-dark fw-bold">{childrenOptions.length} Options</span>
                  </div>

                  <form onSubmit={handleAddChildOption} className="d-flex gap-2 mb-4">
                    <input
                      type="text"
                      className="form-control rm-input"
                      placeholder="e.g. Child 5+ (Teenager)"
                      value={newChildInput}
                      onChange={(e) => setNewChildInput(e.target.value)}
                      required
                    />
                    <button type="submit" className="btn btn-action-gold rounded-pill px-3 fw-bold flex-shrink-0">
                      <i className="fa fa-plus me-1" /> Add Option
                    </button>
                  </form>

                  <div className="d-flex flex-column gap-2">
                    {childrenOptions.map((opt, idx) => (
                      <div
                        key={`child-opt-${idx}`}
                        className="d-flex align-items-center justify-content-between p-3 rounded-3"
                        style={{ background: "#FAF6F0", border: "1px solid rgba(212, 175, 55, 0.3)" }}
                      >
                        <span className="fw-semibold text-dark"><i className="fa fa-check text-success me-2" />{opt}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger rounded-circle"
                          title="Remove Option"
                          style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}
                          onClick={() => handleRemoveChildOption(idx)}
                          disabled={childrenOptions.length <= 1}
                        >
                          <i className="fa fa-trash-alt" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ RESORT SETTINGS TAB ═══════════ */}
        {activeTab === "settings" && (
          <div className="admin-mgmt-section">
            <div className="row justify-content-center g-4">
              <div className="col-12 col-lg-10">
                <form onSubmit={handleSaveResortSettings}>
                  
                  {/* Card 1: Official Contact Information */}
                  <div className="p-4 p-md-5 rounded-4 bg-white shadow-sm mb-4" style={{ border: "1.5px solid rgba(212, 175, 55, 0.4)" }}>
                    <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom border-warning-subtle">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", background: "rgba(254, 161, 22, 0.15)", color: "#FEA116", flexShrink: 0 }}>
                        <i className="fa fa-phone-alt fa-lg" />
                      </div>
                      <div>
                        <h4 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>
                          Official Contact Information
                        </h4>
                        <p className="text-muted small m-0">
                          Update resort phone numbers, WhatsApp concierge, email address, and physical location.
                        </p>
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">
                          FOOTER PHONE NUMBER(S) <span className="badge bg-gold text-dark ms-1">Supports & or /</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white"><i className="fa fa-phone text-success" /></span>
                          <input
                            type="text"
                            className="form-control rm-input"
                            placeholder="+91 9602588882 / +91 9876543210"
                            value={resortPhone}
                            onChange={(e) => setResortPhone(e.target.value)}
                            required
                          />
                        </div>
                        <span className="small text-muted d-block mt-1">
                          <i className="fa fa-info-circle text-warning me-1" />Controls site footer phone row. Add multiple with <code>/</code> or <code>&</code>.
                        </span>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">WHATSAPP CONCIERGE NUMBER</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white"><i className="fab fa-whatsapp text-success" /></span>
                          <input
                            type="text"
                            className="form-control rm-input"
                            placeholder="919602588882"
                            value={resortWhatsapp}
                            onChange={(e) => setResortWhatsapp(e.target.value)}
                            required
                          />
                        </div>
                        <span className="small text-muted d-block mt-1">
                          Used for WhatsApp floating widget & direct chat links.
                        </span>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">
                          FOOTER EMAIL ADDRESS(ES) <span className="badge bg-gold text-dark ms-1">Supports & or /</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white"><i className="fa fa-envelope text-warning" /></span>
                          <input
                            type="text"
                            className="form-control rm-input"
                            placeholder="info@harikunjresort.com & reservation@harikunjresort.com"
                            value={resortEmail}
                            onChange={(e) => setResortEmail(e.target.value)}
                            required
                          />
                        </div>
                        <span className="small text-muted d-block mt-1">
                          <i className="fa fa-info-circle text-warning me-1" />Controls site footer email row. Add multiple with <code>/</code> or <code>&</code>.
                        </span>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">BOOKING DESK EMAIL(S) (Supports & or /)</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white"><i className="fa fa-concierge-bell text-warning" /></span>
                          <input
                            type="text"
                            className="form-control rm-input"
                            placeholder="reservation@harikunjresort.com & booking@harikunjresort.com"
                            value={bookingEmail}
                            onChange={(e) => setBookingEmail(e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">TECHNICAL SUPPORT EMAIL(S) (Supports & or /)</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white"><i className="fa fa-headset text-warning" /></span>
                          <input
                            type="text"
                            className="form-control rm-input"
                            placeholder="reservation@harikunjresort.com / tech@harikunjresort.com"
                            value={techEmail}
                            onChange={(e) => setTechEmail(e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">GENERAL INQUIRIES EMAIL(S) (Supports & or /)</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white"><i className="fa fa-envelope-open-text text-warning" /></span>
                          <input
                            type="text"
                            className="form-control rm-input"
                            placeholder="info@harikunjresort.com & reservation@harikunjresort.com"
                            value={generalEmail}
                            onChange={(e) => setGeneralEmail(e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="form-label text-muted small fw-bold">RESORT PHYSICAL LOCATION</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white"><i className="fa fa-map-marker-alt text-danger" /></span>
                          <input
                            type="text"
                            className="form-control rm-input"
                            placeholder="Gopal Badi, Chopasni, Jodhpur"
                            value={resortAddress}
                            onChange={(e) => setResortAddress(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Social Media Links & Icon Visibility Controls */}
                  <div className="p-4 p-md-5 rounded-4 bg-white shadow-sm mb-4" style={{ border: "1.5px solid rgba(212, 175, 55, 0.4)" }}>
                    <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom border-warning-subtle">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", background: "rgba(30, 62, 114, 0.12)", color: "#1E3E72", flexShrink: 0 }}>
                        <i className="fa fa-share-alt fa-lg" />
                      </div>
                      <div>
                        <h4 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>
                          Social Media Links & Icon Visibility Controls
                        </h4>
                        <p className="text-muted small m-0">
                          Edit social profile URLs and toggle whether each icon appears in the header and footer.
                        </p>
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <label className="form-label text-muted small fw-bold m-0"><i className="fab fa-instagram me-1 text-danger" /> INSTAGRAM URL</label>
                          <div className="form-check form-switch m-0">
                            <input className="form-check-input" type="checkbox" checked={showInstagram} onChange={(e) => setShowInstagram(e.target.checked)} id="toggleInsta" />
                            <label className="form-check-label small fw-semibold ms-1" htmlFor="toggleInsta">{showInstagram ? "Visible" : "Hidden"}</label>
                          </div>
                        </div>
                        <input type="url" className="form-control rm-input" value={resortInstagram} onChange={(e) => setResortInstagram(e.target.value)} required />
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <label className="form-label text-muted small fw-bold m-0"><i className="fab fa-facebook me-1 text-primary" /> FACEBOOK URL</label>
                          <div className="form-check form-switch m-0">
                            <input className="form-check-input" type="checkbox" checked={showFacebook} onChange={(e) => setShowFacebook(e.target.checked)} id="toggleFb" />
                            <label className="form-check-label small fw-semibold ms-1" htmlFor="toggleFb">{showFacebook ? "Visible" : "Hidden"}</label>
                          </div>
                        </div>
                        <input type="url" className="form-control rm-input" value={resortFacebook} onChange={(e) => setResortFacebook(e.target.value)} required />
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <label className="form-label text-muted small fw-bold m-0"><i className="fab fa-youtube me-1 text-danger" /> YOUTUBE CHANNEL URL</label>
                          <div className="form-check form-switch m-0">
                            <input className="form-check-input" type="checkbox" checked={showYoutube} onChange={(e) => setShowYoutube(e.target.checked)} id="toggleYt" />
                            <label className="form-check-label small fw-semibold ms-1" htmlFor="toggleYt">{showYoutube ? "Visible" : "Hidden"}</label>
                          </div>
                        </div>
                        <input type="url" className="form-control rm-input" value={resortYoutube} onChange={(e) => setResortYoutube(e.target.value)} required />
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <label className="form-label text-muted small fw-bold m-0"><i className="fas fa-map-marker-alt me-1 text-danger" /> GOOGLE MAPS LINK</label>
                          <div className="form-check form-switch m-0">
                            <input className="form-check-input" type="checkbox" checked={showLocation} onChange={(e) => setShowLocation(e.target.checked)} id="toggleLoc" />
                            <label className="form-check-label small fw-semibold ms-1" htmlFor="toggleLoc">{showLocation ? "Visible" : "Hidden"}</label>
                          </div>
                        </div>
                        <input type="url" className="form-control rm-input" value={resortLocationUrl} onChange={(e) => setResortLocationUrl(e.target.value)} required />
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Site Widgets & Floating Controls */}
                  <div className="p-4 p-md-5 rounded-4 bg-white shadow-sm mb-4" style={{ border: "1.5px solid rgba(212, 175, 55, 0.4)" }}>
                    <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom border-warning-subtle">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", background: "rgba(46, 204, 113, 0.12)", color: "#2ecc71", flexShrink: 0 }}>
                        <i className="fa fa-toggle-on fa-lg" />
                      </div>
                      <div>
                        <h4 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>
                          Floating Quick Contact & Footer Controls
                        </h4>
                        <p className="text-muted small m-0">
                          Toggle visibility of floating WhatsApp / Call buttons and Footer VIP Newsletter subscription banner.
                        </p>
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <div className="p-3 rounded-3 border d-flex align-items-center justify-content-between" style={{ background: "#FAF6F0" }}>
                          <div>
                            <strong style={{ color: "#0B1D37", display: "block" }}>Floating WhatsApp & Call Widget</strong>
                            <span className="small text-muted">Show floating quick contact buttons at bottom right</span>
                          </div>
                          <div className="form-check form-switch m-0">
                            <input className="form-check-input" type="checkbox" checked={showFloatingContact} onChange={(e) => setShowFloatingContact(e.target.checked)} id="toggleFloat" style={{ transform: "scale(1.25)" }} />
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="p-3 rounded-3 border d-flex align-items-center justify-content-between" style={{ background: "#FAF6F0" }}>
                          <div>
                            <strong style={{ color: "#0B1D37", display: "block" }}>Footer Newsletter Banner</strong>
                            <span className="small text-muted">Show Royal VIP updates subscription box in footer</span>
                          </div>
                          <div className="form-check form-switch m-0">
                            <input className="form-check-input" type="checkbox" checked={showNewsletter} onChange={(e) => setShowNewsletter(e.target.checked)} id="toggleNews" style={{ transform: "scale(1.25)" }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="btn btn-action-gold w-100 rounded-pill py-3 fw-bold shadow-sm" style={{ fontSize: "1.05rem" }}>
                    <i className="fa fa-save me-2" /> Save & Update All Resort Settings Live
                  </button>

                  {saveSettingsMsg && (
                    <div className="mt-3 text-center small fw-bold text-success p-3 rounded-3" style={{ background: "rgba(46, 204, 113, 0.1)", border: "1px solid rgba(46, 204, 113, 0.3)" }}>
                      {saveSettingsMsg}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        )}
          </div>
        </main>
      </div>

      {/* Edit Room Popup Modal */}
      {editingRoom && (
        <div className="unique-popup-container" style={{ zIndex: 10000 }}>
          <div className="unique-popup-content p-4" style={{ borderRadius: "24px", maxWidth: "500px" }}>
            <button className="unique-close-btn" onClick={() => setEditingRoom(null)} style={{ top: "20px", right: "20px" }}>
              ✕
            </button>
            
            <h4 className="rm-section-title mb-4 text-warning" style={{ fontSize: "1.4rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              <i className="fa fa-edit me-2" />Edit Room Details
            </h4>
            
            <form onSubmit={handleSaveRoom}>
              <div className="mb-3">
                <label className="form-label text-light opacity-75 small fw-bold">ROOM TYPE NAME</label>
                <input
                  type="text"
                  className="form-control rm-input bg-dark text-light border-secondary"
                  value={editRoomType}
                  onChange={(e) => setEditRoomType(e.target.value)}
                  required
                />
              </div>
              
              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label className="form-label text-light opacity-75 small fw-bold">PRICE PER NIGHT (₹)</label>
                  <input
                    type="number"
                    className="form-control rm-input bg-dark text-light border-secondary"
                    value={editRoomPrice}
                    onChange={(e) => setEditRoomPrice(e.target.value)}
                    min="1"
                    required
                  />
                </div>
                <div className="col-6">
                  <label className="form-label text-light opacity-75 small fw-bold">TOTAL ROOMS</label>
                  <input
                    type="number"
                    className="form-control rm-input bg-dark text-light border-secondary"
                    value={editRoomInventory}
                    onChange={(e) => setEditRoomInventory(e.target.value)}
                    min="0"
                    required
                  />
                </div>
              </div>
              
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <label className="form-label text-light opacity-75 small fw-bold">BOOKED COUNT</label>
                  <input
                    type="number"
                    className="form-control rm-input bg-dark text-light border-secondary"
                    value={editBookedCount}
                    onChange={(e) => setEditBookedCount(e.target.value)}
                    min="0"
                    required
                  />
                </div>
                <div className="col-6">
                  <label className="form-label text-light opacity-75 small fw-bold">STATUS</label>
                  <select
                    className="form-select rm-input bg-dark text-light border-secondary"
                    value={editRoomStatus}
                    onChange={(e) => setEditRoomStatus(e.target.value)}
                    required
                  >
                    <option value="Available">Available</option>
                    <option value="Full">Full</option>
                  </select>
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-6">
                  <label className="form-label text-light opacity-75 small fw-bold">DISCOUNT (%)</label>
                  <input
                    type="number"
                    className="form-control rm-input bg-dark text-light border-secondary"
                    placeholder="e.g. 25"
                    value={editDiscountPercent}
                    onChange={(e) => setEditDiscountPercent(e.target.value)}
                    min="0"
                    max="100"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label text-light opacity-75 small fw-bold">PROMO BADGE / TAGLINE</label>
                  <input
                    type="text"
                    className="form-control rm-input bg-dark text-light border-secondary"
                    placeholder="e.g. 25% OFF TODAY"
                    value={editPromoText}
                    onChange={(e) => setEditPromoText(e.target.value)}
                  />
                </div>
              </div>
              
              <button type="submit" className="btn btn-action-gold w-100 rounded-pill py-2 font-weight-bold" disabled={isSavingRoom}>
                {isSavingRoom ? <><i className="fa fa-spinner fa-spin me-2" />Saving...</> : <><i className="fa fa-save me-2" />Save Changes</>}
              </button>
              
              {editRoomMsg && (
                <div className="mt-3 text-center small fw-bold" style={{ color: editRoomMsg.startsWith("❌") ? "#ff4d4d" : "#2ecc71" }}>
                  {editRoomMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ═══════════ BOOKING DETAIL POPUP MODAL ═══════════ */}
      {selectedBookingModal && (
        <div className="unique-popup-container" style={{ zIndex: 100000 }}>
          <div className="unique-popup-card p-4 rounded-4 bg-white shadow-lg border border-warning" style={{ maxWidth: "560px", width: "92%" }}>
            <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
              <div>
                <span className="badge bg-gold text-dark fw-bold mb-1">ID: {selectedBookingModal.bookingId}</span>
                <h4 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>{selectedBookingModal.guestName || "Guest Reservation"}</h4>
              </div>
              <button className="btn-close" onClick={() => setSelectedBookingModal(null)} />
            </div>

            <div className="d-flex flex-column gap-2 mb-4">
              <div className="p-3 rounded-3" style={{ background: "#FAF6F0" }}>
                <strong className="text-warning small text-uppercase d-block mb-1">STAY DATES & ROOM</strong>
                <div className="fw-bold" style={{ color: "#0B1D37" }}>{selectedBookingModal.roomType}</div>
                <div className="small text-muted">{selectedBookingModal.checkIn} ➔ {selectedBookingModal.checkOut}</div>
                <div className="small text-muted">{selectedBookingModal.adults} Adults, {selectedBookingModal.children} Children</div>
              </div>

              <div className="p-3 rounded-3" style={{ background: "#FAF6F0" }}>
                <strong className="text-warning small text-uppercase d-block mb-1">GUEST CONTACT DETAILS</strong>
                <div><i className="fa fa-envelope me-2 text-warning" /><strong>Email:</strong> <a href={`mailto:${selectedBookingModal.guestEmail}`} style={{ color: "#1E3E72" }}>{selectedBookingModal.guestEmail || "N/A"}</a></div>
                <div><i className="fa fa-phone me-2 text-success" /><strong>Phone:</strong> <a href={`tel:${selectedBookingModal.guestNumber}`} style={{ color: "#1E3E72" }}>{selectedBookingModal.guestNumber || "N/A"}</a></div>
              </div>

              <div className="p-3 rounded-3 d-flex align-items-center justify-content-between" style={{ background: "#FAF6F0" }}>
                <div>
                  <strong className="text-warning small text-uppercase d-block">TOTAL TARIFF</strong>
                  <h4 className="m-0 fw-bold text-success">₹{Number(selectedBookingModal.totalCost).toLocaleString("en-IN")}</h4>
                </div>
                <div>
                  <strong className="text-warning small text-uppercase d-block mb-1">STATUS</strong>
                  <HKStatusDropdown
                    value={selectedBookingModal.status || "Pending"}
                    options={BOOKING_STATUS_OPTIONS}
                    onChange={(newVal) => {
                      handleStatusChange(selectedBookingModal.bookingId, newVal);
                      setSelectedBookingModal({ ...selectedBookingModal, status: newVal });
                    }}
                    disabled={updatingId === selectedBookingModal.bookingId}
                  />
                </div>
              </div>
            </div>

            <div className="d-flex gap-2">
              <a
                href={`https://wa.me/${String(selectedBookingModal.guestNumber || "").replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-success flex-grow-1 rounded-pill fw-bold py-2"
              >
                <i className="fab fa-whatsapp me-2" /> Chat on WhatsApp
              </a>
              <button className="btn btn-outline-dark rounded-pill px-4" onClick={() => setSelectedBookingModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ CALLBACK DETAIL POPUP MODAL ═══════════ */}
      {selectedCallbackModal && (
        <div className="unique-popup-container" style={{ zIndex: 100000 }}>
          <div className="unique-popup-card p-4 rounded-4 bg-white shadow-lg border border-warning" style={{ maxWidth: "520px", width: "92%" }}>
            <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
              <div>
                <span className="badge bg-warning text-dark fw-bold mb-1">Concierge Callback</span>
                <h4 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>{selectedCallbackModal.fullName || "Guest Inquiry"}</h4>
              </div>
              <button className="btn-close" onClick={() => setSelectedCallbackModal(null)} />
            </div>

            <div className="d-flex flex-column gap-2 mb-4">
              <div className="p-3 rounded-3" style={{ background: "#FAF6F0" }}>
                <strong className="text-warning small text-uppercase d-block mb-1">CONTACT DETAILS</strong>
                <div><i className="fa fa-envelope me-2 text-warning" /><strong>Email:</strong> <a href={`mailto:${selectedCallbackModal.email}`} style={{ color: "#1E3E72" }}>{selectedCallbackModal.email || "N/A"}</a></div>
                <div><i className="fa fa-phone me-2 text-success" /><strong>Phone:</strong> <a href={`tel:${selectedCallbackModal.number}`} style={{ color: "#1E3E72" }}>{selectedCallbackModal.number || "N/A"}</a></div>
                <div className="small text-muted mt-1"><i className="fa fa-clock me-1" />Logged: {selectedCallbackModal.timestamp ? new Date(selectedCallbackModal.timestamp).toLocaleString() : "-"}</div>
              </div>

              <div className="p-3 rounded-3 d-flex align-items-center justify-content-between" style={{ background: "#FAF6F0" }}>
                <strong className="text-warning small text-uppercase">UPDATE STATUS</strong>
                <HKStatusDropdown
                  value={selectedCallbackModal.status || "Pending"}
                  options={GENERAL_STATUS_OPTIONS}
                  onChange={(newVal) => {
                    handleCallbackStatusChange(selectedCallbackModal.id, newVal);
                    setSelectedCallbackModal({ ...selectedCallbackModal, status: newVal });
                  }}
                  disabled={updatingId === selectedCallbackModal.id}
                />
              </div>
            </div>

            <div className="d-flex gap-2">
              <a
                href={`https://wa.me/${String(selectedCallbackModal.number || "").replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-success flex-grow-1 rounded-pill fw-bold py-2"
              >
                <i className="fab fa-whatsapp me-2" /> WhatsApp Guest
              </a>
              <button className="btn btn-outline-dark rounded-pill px-4" onClick={() => setSelectedCallbackModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ SUPPORT QUERY DETAIL POPUP MODAL ═══════════ */}
      {selectedQueryModal && (
        <div className="unique-popup-container" style={{ zIndex: 100000 }}>
          <div className="unique-popup-card p-4 rounded-4 bg-white shadow-lg border border-warning" style={{ maxWidth: "540px", width: "92%" }}>
            <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
              <div>
                <span className="badge bg-info text-white fw-bold mb-1">Support Query</span>
                <h4 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>{selectedQueryModal.fullName || "Guest Support"}</h4>
              </div>
              <button className="btn-close" onClick={() => setSelectedQueryModal(null)} />
            </div>

            <div className="d-flex flex-column gap-2 mb-4">
              <div className="p-3 rounded-3" style={{ background: "#FAF6F0" }}>
                <strong className="text-warning small text-uppercase d-block mb-1">CONTACT DETAILS</strong>
                <div><i className="fa fa-envelope me-2 text-warning" /><strong>Email:</strong> <a href={`mailto:${selectedQueryModal.email}`} style={{ color: "#1E3E72" }}>{selectedQueryModal.email || "N/A"}</a></div>
                <div><i className="fa fa-phone me-2 text-success" /><strong>Phone:</strong> <a href={`tel:${selectedQueryModal.number}`} style={{ color: "#1E3E72" }}>{selectedQueryModal.number || "N/A"}</a></div>
                <div className="small text-muted mt-1"><i className="fa fa-clock me-1" />Logged: {selectedQueryModal.timestamp ? new Date(selectedQueryModal.timestamp).toLocaleString() : "-"}</div>
              </div>

              <div className="p-3 rounded-3" style={{ background: "#FAF6F0" }}>
                <strong className="text-warning small text-uppercase d-block mb-1">GUEST MESSAGE</strong>
                <p className="m-0 text-dark small" style={{ lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{selectedQueryModal.message || "No message content provided."}</p>
              </div>

              <div className="p-3 rounded-3 d-flex align-items-center justify-content-between" style={{ background: "#FAF6F0" }}>
                <strong className="text-warning small text-uppercase">UPDATE STATUS</strong>
                <HKStatusDropdown
                  value={selectedQueryModal.status || "Pending"}
                  options={GENERAL_STATUS_OPTIONS}
                  onChange={(newVal) => {
                    handleSupportStatusChange(selectedQueryModal.id, newVal);
                    setSelectedQueryModal({ ...selectedQueryModal, status: newVal });
                  }}
                  disabled={updatingId === selectedQueryModal.id}
                />
              </div>
            </div>

            <div className="d-flex gap-2">
              <a
                href={`https://wa.me/${String(selectedQueryModal.number || "").replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-success flex-grow-1 rounded-pill fw-bold py-2"
              >
                <i className="fab fa-whatsapp me-2" /> WhatsApp Response
              </a>
              <button className="btn btn-outline-dark rounded-pill px-4" onClick={() => setSelectedQueryModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ ADD ROOM POPUP MODAL ═══════════ */}
      {isAddRoomModalOpen && (
        <div className="unique-popup-container" style={{ zIndex: 100000 }}>
          <div className="unique-popup-card p-4 rounded-4 bg-white shadow-lg border border-warning" style={{ maxWidth: "520px", width: "92%" }}>
            <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
              <div>
                <span className="badge bg-gold text-dark fw-bold mb-1">Inventory Management</span>
                <h4 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>Add New Room Category</h4>
              </div>
              <button className="btn-close" onClick={() => setIsAddRoomModalOpen(false)} />
            </div>

            <form onSubmit={handleAddRoom}>
              <div className="mb-3">
                <label className="form-label text-muted small fw-bold">ROOM TYPE NAME</label>
                <input
                  type="text"
                  className="form-control rm-input"
                  placeholder="e.g. Executive Suite, Heritage Villa"
                  value={newRoomType}
                  onChange={(e) => setNewRoomType(e.target.value)}
                  required
                />
              </div>

              <div className="row g-3 mb-4">
                <div className="col-6">
                  <label className="form-label text-muted small fw-bold">PRICE PER NIGHT (₹)</label>
                  <input
                    type="number"
                    className="form-control rm-input"
                    placeholder="e.g. 5999"
                    value={newRoomPrice}
                    onChange={(e) => setNewRoomPrice(e.target.value)}
                    min="1"
                    required
                  />
                </div>
                <div className="col-6">
                  <label className="form-label text-muted small fw-bold">TOTAL ROOMS ALLOCATED</label>
                  <input
                    type="number"
                    className="form-control rm-input"
                    placeholder="e.g. 8"
                    value={newRoomInventory}
                    onChange={(e) => setNewRoomInventory(e.target.value)}
                    min="1"
                    required
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-action-gold w-100 rounded-pill py-2 font-weight-bold" disabled={isAddingRoom}>
                {isAddingRoom ? <><i className="fa fa-spinner fa-spin me-2" />Creating Room...</> : <><i className="fa fa-plus-circle me-2" />Add Room Category</>}
              </button>

              {addRoomMsg && (
                <div className="mt-3 text-center small fw-bold" style={{ color: addRoomMsg.startsWith("❌") ? "#ff4d4d" : "#2ecc71" }}>
                  {addRoomMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ═══════════ ADD ADMIN POPUP MODAL ═══════════ */}
      {isAddAdminModalOpen && (
        <div className="unique-popup-container" style={{ zIndex: 100000 }}>
          <div className="unique-popup-card p-4 rounded-4 bg-white shadow-lg border border-warning" style={{ maxWidth: "560px", width: "92%" }}>
            <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3">
              <div>
                <span className="badge bg-gold text-dark fw-bold mb-1">Access Control</span>
                <h4 className="m-0 fw-bold" style={{ color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>Create New Admin Account</h4>
              </div>
              <button className="btn-close" onClick={() => setIsAddAdminModalOpen(false)} />
            </div>

            <form onSubmit={handleCreateAdmin}>
              <div className="mb-3">
                <label className="form-label text-muted small fw-bold">FULL NAME</label>
                <div className="input-group">
                  <span className="input-group-text bg-white"><i className="fa fa-user text-warning" /></span>
                  <input
                    type="text"
                    className="form-control rm-input"
                    placeholder="e.g. Raj Kumar"
                    value={newAdminName}
                    onChange={(e) => setNewAdminName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                  <label className="form-label text-muted small fw-bold">EMAIL ADDRESS</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="fa fa-envelope text-warning" /></span>
                    <input
                      type="email"
                      className="form-control rm-input"
                      placeholder="e.g. manager@harikunjresort.com"
                      value={newAdminEmail}
                      onChange={(e) => setNewAdminEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label text-muted small fw-bold">PHONE NUMBER</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="fa fa-phone text-warning" /></span>
                    <input
                      type="text"
                      className="form-control rm-input"
                      placeholder="e.g. +91 9876543210"
                      value={newAdminPhone}
                      onChange={(e) => setNewAdminPhone(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-12 col-md-6">
                  <label className="form-label text-muted small fw-bold">PASSWORD (min 8 chars)</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="fa fa-lock text-warning" /></span>
                    <input
                      type={showAdminPwd ? "text" : "password"}
                      className="form-control rm-input"
                      placeholder="••••••••"
                      value={newAdminPassword}
                      onChange={(e) => setNewAdminPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="input-group-text bg-white"
                      onClick={() => setShowAdminPwd(!showAdminPwd)}
                    >
                      <i className={`fa ${showAdminPwd ? "fa-eye-slash" : "fa-eye"} text-muted`} />
                    </button>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label text-muted small fw-bold">CONFIRM PASSWORD</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="fa fa-lock text-warning" /></span>
                    <input
                      type={showAdminPwd ? "text" : "password"}
                      className="form-control rm-input"
                      placeholder="••••••••"
                      value={newAdminConfirm}
                      onChange={(e) => setNewAdminConfirm(e.target.value)}
                      required
                      minLength={8}
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-action-gold w-100 rounded-pill py-2 font-weight-bold" disabled={isCreatingAdmin}>
                {isCreatingAdmin ? <><i className="fa fa-spinner fa-spin me-2" />Provisioning Account...</> : <><i className="fa fa-user-plus me-2" />Create Admin Account</>}
              </button>

              {createAdminMsg && (
                <div className="mt-3 text-center small fw-bold" style={{ color: createAdminMsg.startsWith("❌") ? "#ff4d4d" : "#2ecc71" }}>
                  {createAdminMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      <style>{`
        /* ═══════════ CRITICAL POSITION FIXED CONTAINING BLOCK FIX ═══════════ */
        .hk-page-entrance-wrapper {
          transform: none !important;
          animation: none !important;
          will-change: auto !important;
        }

        /* ═══════════ GLOBAL SCROLLBAR HIDE ═══════════ */
        html, body {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        html::-webkit-scrollbar, body::-webkit-scrollbar {
          display: none !important;
        }

        /* ═══════════ SIDEBAR LAYOUT SYSTEM ═══════════ */
        .hk-dashboard-wrapper {
          display: flex;
          min-height: 100vh;
          width: 100%;
          background: #FAF6F0;
          position: relative;
          color: #0B1D37;
          overflow: visible;
        }

        /* Sidebar container */
        .hk-sidebar {
          width: 280px;
          min-width: 280px;
          max-width: 280px;
          height: 100vh;
          background: #FFFFFF !important;
          border-right: 1.5px solid rgba(212, 175, 55, 0.35) !important;
          box-shadow: 4px 0 24px rgba(11, 29, 55, 0.06);
          color: #0B1D37;
          display: flex;
          flex-direction: column;
          padding: 16px 14px;
          flex-shrink: 0;
          z-index: 1000;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          overflow: hidden;
        }

        .hk-sidebar-brand {
          text-align: center;
          padding-bottom: 10px;
          border-bottom: 1.5px solid rgba(212, 175, 55, 0.2);
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
        }
        .hk-sidebar-brand .logo-main {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          color: #0B1D37 !important;
          font-size: 1.3rem;
          letter-spacing: 0.05em;
        }
        .hk-sidebar-brand .logo-sub {
          font-family: 'Poppins', sans-serif;
          font-size: 0.65rem;
          font-weight: 600;
          color: #FEA116 !important;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-top: 1px;
        }

        .hk-sidebar-profile {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 10px 0 10px;
          border-bottom: 1.5px solid rgba(212, 175, 55, 0.2);
          flex-shrink: 0;
        }
        .profile-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #0B1D37 0%, #102A4D 100%);
          color: #FEA116;
          font-family: 'Cinzel', serif;
          font-size: 1.1rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 14px rgba(11, 29, 55, 0.15);
          border: 2px solid #FEA116;
          margin-bottom: 6px;
        }
        .profile-name {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          font-size: 0.88rem;
          color: #0B1D37;
        }
        .profile-email {
          font-family: 'Poppins', sans-serif;
          font-size: 0.7rem;
          color: #53647C;
          margin-top: 1px;
        }

        .hk-sidebar-nav {
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 10px 0;
          flex: 1 1 auto;
          overflow-y: auto;
          min-height: 0;
        }
        .hk-sidebar-nav::-webkit-scrollbar {
          width: 4px;
        }
        .hk-sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(212, 175, 55, 0.35);
          border-radius: 4px;
        }
        .nav-item {
          background: transparent;
          border: none;
          color: #0B1D37;
          opacity: 0.85;
          text-align: left;
          padding: 9px 12px;
          border-radius: 10px;
          font-family: 'Poppins', sans-serif;
          font-size: 0.84rem;
          font-weight: 600;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          width: 100%;
          cursor: pointer;
          flex-shrink: 0;
        }
        .nav-item:hover {
          background: rgba(254, 161, 22, 0.1);
          color: #FEA116;
          opacity: 1;
          transform: translateX(3px);
        }
        .nav-item--active {
          background: linear-gradient(135deg, #0B1D37 0%, #102A4D 100%) !important;
          color: #FEA116 !important;
          border: 1.5px solid #FEA116;
          opacity: 1;
          box-shadow: 0 4px 12px rgba(11, 29, 55, 0.18);
        }

        .hk-sidebar-footer {
          padding-top: 10px;
          border-top: 1.5px solid rgba(212, 175, 55, 0.2);
          flex-shrink: 0;
          background: #FFFFFF;
          position: sticky;
          bottom: 0;
          margin-top: auto;
        }
        .hk-btn-logout {
          background: rgba(231, 76, 60, 0.08);
          color: #e74c3c;
          border: 1.5px solid rgba(231, 76, 60, 0.3);
          border-radius: 10px;
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          padding: 9px 0;
          transition: all 0.2s ease;
        }
        .hk-btn-logout:hover {
          background: #e74c3c;
          color: #FAF6F0;
          box-shadow: 0 4px 14px rgba(231, 76, 60, 0.2);
        }

        /* ═══════════ LUXURY CUSTOM STATUS DROPDOWN STYLES ═══════════ */
        .hk-custom-dropdown-container {
          position: relative;
          display: inline-block;
        }

        .hk-custom-dropdown-trigger {
          display: inline-flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 14px;
          border-radius: 24px;
          font-family: 'Poppins', sans-serif;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.25s ease;
          border: 1.5px solid rgba(212, 175, 55, 0.4);
          background: #FFFFFF !important;
          color: #0B1D37 !important;
          box-shadow: 0 2px 8px rgba(11, 29, 55, 0.05);
        }

        .hk-custom-dropdown-trigger:hover {
          border-color: #FEA116;
          box-shadow: 0 4px 12px rgba(254, 161, 22, 0.15);
          transform: translateY(-1px);
        }

        /* Status Badge Colors */
        .hk-status-badge--pending    { border-color: #f0ad4e !important; color: #d9822b !important; background: #FFFDF9 !important; }
        .hk-status-badge--confirmed  { border-color: #2ecc71 !important; color: #27ae60 !important; background: #F8FFF9 !important; }
        .hk-status-badge--resolved   { border-color: #2ecc71 !important; color: #27ae60 !important; background: #F8FFF9 !important; }
        .hk-status-badge--completed  { border-color: #17a2b8 !important; color: #117a8b !important; background: #F6FCFD !important; }
        .hk-status-badge--inprogress { border-color: #00bcd4 !important; color: #00838f !important; background: #F4FCFD !important; }
        .hk-status-badge--cancelled  { border-color: #e74c3c !important; color: #c0392b !important; background: #FFF8F8 !important; }

        .hk-custom-dropdown-menu {
          position: absolute;
          top: calc(100% + 6px);
          right: 0;
          min-width: 160px;
          background: #FFFFFF !important;
          border: 1.5px solid rgba(212, 175, 55, 0.4) !important;
          border-radius: 14px !important;
          padding: 6px !important;
          z-index: 99999 !important;
          box-shadow: 0 10px 30px rgba(11, 29, 55, 0.15) !important;
          animation: hkFadeInDown 0.18s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .hk-custom-dropdown-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 8px 12px;
          border: none;
          background: transparent;
          color: #0B1D37 !important;
          font-family: 'Poppins', sans-serif;
          font-size: 0.84rem;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }

        .hk-custom-dropdown-item:hover {
          background: #FAF6F0 !important;
          color: #FEA116 !important;
        }

        .hk-custom-dropdown-item--active {
          background: rgba(254, 161, 22, 0.12) !important;
          color: #E38A12 !important;
          font-weight: 700;
        }

        @keyframes hkFadeInDown {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Backdrop overlay */
        .hk-sidebar-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(11, 29, 55, 0.6);
          backdrop-filter: blur(4px);
          z-index: 99998;
        }

        /* Main Content container with independent desktop margin alignment */
        .hk-main-content {
          margin-left: 280px !important;
          width: calc(100% - 280px) !important;
          min-height: 100vh;
          background: linear-gradient(135deg, #FAF6F0 0%, #F5EFEB 100%);
          position: relative;
          box-sizing: border-box;
        }

        /* ═══════════ RESPONSIVE SIDEBAR & MOBILE DRAWER ═══════════ */
        @media (max-width: 991.98px) {
          .hk-dashboard-wrapper {
            position: relative !important;
            height: auto !important;
            min-height: 100vh !important;
            width: 100% !important;
            overflow: visible !important;
          }
          .hk-sidebar {
            position: fixed !important;
            top: 0;
            left: 0;
            bottom: 0;
            width: 280px !important;
            height: 100vh !important;
            z-index: 99999 !important;
            transform: translateX(-100%) !important;
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
            box-shadow: 8px 0 25px rgba(0, 0, 0, 0.25) !important;
          }
          .hk-sidebar--open {
            transform: translateX(0) !important;
          }
          .hk-sidebar-backdrop {
            position: fixed !important;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            width: 100vw !important;
            height: 100vh !important;
            background: rgba(11, 29, 55, 0.6) !important;
            backdrop-filter: blur(4px) !important;
            z-index: 99998 !important;
          }
          .hk-main-content {
            margin-left: 0 !important;
            width: 100% !important;
            max-width: 100vw !important;
            min-height: 100vh !important;
            height: auto !important;
            overflow-y: visible !important;
            padding: 8px !important;
          }
        }

        @media (min-width: 992px) {
          .hk-mobile-toggle-btn {
            display: none !important;
          }
        }

        /* ═══════════ ADMIN CONTENT STYLES ═══════════ */
        .dashboard-title {
          font-family: 'Cinzel', serif;
          font-weight: 700;
        }
        .dashboard-subtitle {
          font-family: 'Poppins', sans-serif;
          font-size: 0.95rem;
        }
        .search-input {
          border: 1.5px solid rgba(30, 62, 114, 0.2);
          border-radius: 0 12px 12px 0;
          padding: 10px 18px;
        }
        .search-input:focus {
          border-color: #FEA116;
          box-shadow: none;
        }

        /* ═══════════ LUXURY INPUT GROUP STYLING (ZERO OVERLAP) ═══════════ */
        .admin-mgmt-section .input-group,
        .unique-popup-card .input-group {
          display: flex !important;
          align-items: stretch !important;
          width: 100% !important;
          border-radius: 12px !important;
          box-shadow: none !important;
        }

        .admin-mgmt-section .input-group-text,
        .unique-popup-card .input-group-text {
          background-color: #FAF6F0 !important;
          border: 1.5px solid rgba(212, 175, 55, 0.4) !important;
          border-right: none !important;
          border-top-left-radius: 12px !important;
          border-bottom-left-radius: 12px !important;
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
          padding: 0 16px !important;
          color: #FEA116 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          min-width: 48px !important;
          height: 46px !important;
          flex-shrink: 0 !important;
          margin: 0 !important;
        }

        .admin-mgmt-section .input-group .form-control,
        .admin-mgmt-section .input-group .rm-input,
        .unique-popup-card .input-group .form-control,
        .unique-popup-card .input-group .rm-input {
          border: 1.5px solid rgba(212, 175, 55, 0.4) !important;
          border-left: none !important;
          border-top-right-radius: 12px !important;
          border-bottom-right-radius: 12px !important;
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
          padding: 10px 16px !important;
          height: 46px !important;
          font-family: 'Poppins', sans-serif !important;
          font-size: 0.9rem !important;
          color: #0B1D37 !important;
          background: #FFFFFF !important;
          box-shadow: none !important;
          margin: 0 !important;
        }

        .admin-mgmt-section .input-group:focus-within .input-group-text,
        .unique-popup-card .input-group:focus-within .input-group-text {
          border-color: #FEA116 !important;
          background-color: rgba(254, 161, 22, 0.1) !important;
        }

        .admin-mgmt-section .input-group:focus-within .form-control,
        .admin-mgmt-section .input-group:focus-within .rm-input,
        .unique-popup-card .input-group:focus-within .form-control,
        .unique-popup-card .input-group:focus-within .rm-input {
          border-color: #FEA116 !important;
        }

        .luxury-table-card {
          border: 1.5px solid rgba(212, 175, 55, 0.4);
          background: #FFFFFF !important;
          border-radius: 20px !important;
          box-shadow: 0 8px 24px rgba(11, 29, 55, 0.06);
        }
        
        .add-room-card,
        .admin-mgmt-section .bg-white,
        .hk-dashboard-analytics-section .bg-white,
        .unique-popup-card {
          border-radius: 20px !important;
          border: 1.5px solid rgba(212, 175, 55, 0.35) !important;
        }
        
        /* ═══════════ HK LUXURY LIGHT TABLE STYLES ═══════════ */
        .hk-luxury-table {
          width: 100%;
          border-collapse: collapse;
          margin: 0;
          background: #FFFFFF !important;
        }

        .hk-luxury-table thead th {
          background-color: #0B1D37 !important;
          color: #FEA116 !important;
          font-family: 'Cinzel', serif;
          font-size: 0.85rem;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          padding: 14px 16px !important;
          border-bottom: 2px solid #FEA116 !important;
        }

        .hk-luxury-table tbody tr {
          background-color: #FFFFFF !important;
          transition: background-color 0.25s ease;
        }

        /* LIGHT THEME HOVER ONLY - NO DARK MODE ON HOVER */
        .hk-luxury-table tbody tr:hover {
          background-color: #FAF6F0 !important;
        }
        .hk-luxury-table tbody tr:hover td {
          background-color: #FAF6F0 !important;
          color: #0B1D37 !important;
        }

        .hk-luxury-table tbody td {
          border-bottom: 1px solid rgba(11, 29, 55, 0.08) !important;
          font-family: 'Poppins', sans-serif;
          font-size: 0.92rem;
          color: #0B1D37 !important;
          vertical-align: middle;
          padding: 14px 16px !important;
          background-color: transparent !important;
        }
        .badge.bg-gold {
          background: linear-gradient(135deg, #FEA116, #D4AF37);
          color: #050E1A;
          font-weight: 700;
          font-size: 0.8rem;
          text-transform: uppercase;
        }
        .btn-action-gold {
          background: linear-gradient(135deg, #FEA116, #D4AF37) !important;
          color: #050E1A !important;
          font-weight: 700;
          border-radius: 30px;
          border: none;
          padding: 10px 24px;
        }
        .btn-action-gold:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 16px rgba(254, 161, 22, 0.35);
        }
        .btn-action-danger {
          background: #dc3545;
          color: #fff;
          font-weight: 600;
          border-radius: 30px;
          border: none;
          padding: 10px 24px;
        }
        .btn-action-danger:hover {
          background: #c82333;
        }

        /* ═══════════ ROOM MANAGEMENT STYLES ═══════════ */
        .add-room-card {
          background: #ffffff;
          border: 1.5px solid rgba(212, 175, 55, 0.35);
        }
        .rm-section-title {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          color: #0B2447;
          font-size: 1.1rem;
        }
        .rm-input {
          border: 1.5px solid rgba(30, 62, 114, 0.2);
          border-radius: 12px;
          padding: 10px 14px;
          font-family: 'Poppins', sans-serif;
          transition: border-color 0.2s ease;
        }
        .rm-input:focus {
          border-color: #FEA116;
          box-shadow: 0 0 0 3px rgba(254, 161, 22, 0.1);
        }

        .rm-card {
          background: #FAF6F0;
          border-radius: 20px;
          padding: 24px;
          font-family: 'Poppins', sans-serif;
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(11, 29, 55, 0.06);
        }
        .rm-card--available {
          border: 1.5px solid rgba(46, 204, 113, 0.5);
        }
        .rm-card--full {
          border: 1.5px solid rgba(231, 76, 60, 0.5);
        }
        .rm-card:hover {
          box-shadow: 0 12px 32px rgba(212, 175, 55, 0.15);
          transform: translateY(-3px);
        }

        .rm-room-name {
          font-family: 'Cinzel', serif;
          font-weight: 700;
          color: #0B2447;
          margin: 0;
          font-size: 1.1rem;
        }
        .rm-room-id {
          font-size: 0.72rem;
          color: #53647C;
          background: rgba(11, 29, 55, 0.06);
          padding: 2px 8px;
          border-radius: 6px;
        }

        .rm-status-badge {
          font-size: 0.68rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          padding: 5px 14px;
          border-radius: 99px;
        }
        .rm-status--available {
          background: rgba(46, 204, 113, 0.15);
          color: #27ae60;
          border: 1px solid #27ae60;
        }
        .rm-status--full {
          background: rgba(231, 76, 60, 0.15);
          color: #e74c3c;
          border: 1px solid #e74c3c;
        }

        .rm-price-value {
          font-family: 'Cinzel', serif;
          font-weight: 800;
          font-size: 1.6rem;
          color: #FEA116;
        }
        .rm-price-unit {
          font-size: 0.85rem;
          color: #53647C;
          margin-left: 4px;
        }

        .rm-progress-bar {
          width: 100%;
          height: 8px;
          background: rgba(11, 29, 55, 0.08);
          border-radius: 99px;
          overflow: hidden;
        }
        .rm-progress-fill {
          height: 100%;
          border-radius: 99px;
          transition: width 0.5s ease;
        }
        .rm-fill--good {
          background: linear-gradient(90deg, #2ecc71, #27ae60);
        }
        .rm-fill--warning {
          background: linear-gradient(90deg, #FEA116, #f39c12);
        }
        .rm-fill--danger {
          background: linear-gradient(90deg, #e74c3c, #c0392b);
        }

        /* ═══════════ MOBILE & RESPONSIVE REFINEMENTS ═══════════ */
        
        /* Hide scrollbar for responsive tables */
        .table-responsive {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .table-responsive::-webkit-scrollbar {
          display: none;
        }

        /* Mobile specific style overwrites */
        @media (max-width: 991.98px) {
          .hk-main-content {
            padding: 8px 10px 20px !important;
          }
        }

        /* ═══════════ TABLET & MOBILE REFINEMENTS (< 768px) ═══════════ */
        @media (max-width: 767.98px) {
          /* Convert table to super compact luxury cards */
          .luxury-table-card {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          .luxury-table-card .table-responsive {
            border: none !important;
            overflow-x: visible !important;
          }
          .luxury-table-card table, 
          .luxury-table-card thead, 
          .luxury-table-card tbody, 
          .luxury-table-card th, 
          .luxury-table-card td, 
          .luxury-table-card tr {
            display: block !important;
            width: 100% !important;
          }
          .luxury-table-card thead {
            display: none !important;
          }

          .luxury-table-card tbody tr {
            background: #FFFFFF !important;
            border: 1.5px solid rgba(212, 175, 55, 0.45) !important;
            border-radius: 16px !important;
            margin-bottom: 10px !important;
            padding: 12px 14px !important;
            box-shadow: 0 4px 16px rgba(11, 29, 55, 0.05) !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 6px !important;
            cursor: pointer;
          }

          /* Hide detailed columns on mobile view for ultra-compact card rendering */
          .luxury-table-card tbody td[data-label="Email"],
          .luxury-table-card tbody td[data-label="Phone"],
          .luxury-table-card tbody td[data-label="Message"],
          .luxury-table-card tbody td[data-label="Date Logged"],
          .luxury-table-card tbody td[data-label="Stay Window"],
          .luxury-table-card tbody td[data-label="Room Config"],
          .luxury-table-card tbody td[data-label="Total Tariff"],
          .luxury-table-card tbody td[data-label="Subscription Date"] {
            display: none !important;
          }

          .luxury-table-card tbody td {
            border-bottom: none !important;
            padding: 0 !important;
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            background: transparent !important;
            width: 100% !important;
          }

          .luxury-table-card tbody td::before {
            display: none !important;
          }
        }

          /* General spacing and layouts */
          .container-fluid {
            padding-left: 15px !important;
            padding-right: 15px !important;
          }
          .dashboard-header h2 {
            font-size: 1.5rem !important;
          }
          
          /* Form rows become vertical stack */
          form.row.g-3 {
            flex-direction: column !important;
            gap: 12px !important;
          }
          form.row.g-3 > div {
            width: 100% !important;
          }

          /* Modal adjustments */
          .unique-popup-content {
            width: 95% !important;
            margin: 20px auto !important;
            padding: 20px !important;
          }
        }

        /* ═══════════ SMALL MOBILE REFINEMENTS (< 480px) ═══════════ */
        @media (max-width: 479.98px) {
          .dashboard-header {
            padding: 20px 12px !important;
            text-align: center;
          }
          .dashboard-header h2 {
            font-size: 1.2rem !important;
          }
          .dashboard-header p {
            font-size: 0.78rem !important;
          }
          .dashboard-header .d-flex {
            width: 100%;
            flex-direction: column !important;
            gap: 10px;
          }
          .dashboard-header button {
            width: 100%;
            justify-content: center;
          }
          .profile-avatar {
            width: 56px !important;
            height: 56px !important;
            font-size: 1.3rem !important;
          }
          .profile-name {
            font-size: 0.95rem !important;
          }
          
          /* Tables cell details wrap */
          .luxury-table-card tbody td {
            flex-direction: column !important;
            align-items: flex-start !important;
            text-align: left !important;
          }
          .luxury-table-card tbody td::before {
            margin-bottom: 6px !important;
          }
          .luxury-table-card tbody td > * {
            width: 100% !important;
            text-align: left !important;
          }
          .luxury-table-card tbody td .d-flex {
            justify-content: flex-start !important;
          }

          /* Inventory cards details shrink */
          .rm-card {
            padding: 16px !important;
          }
          .rm-room-name {
            font-size: 1rem !important;
          }
          .rm-price-value {
            font-size: 1.35rem !important;
          }
        }
      `}</style>
    </>
  );
}
